import { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import type {
  OnboardingChecklistSectionSync,
  OnboardingChecklistStatus,
  OnboardingSectionKey,
} from './onboarding-checklist-state';
import {
  buildImportPreview,
  buildTemplateCsv,
  getImportSectionDefinition,
  IMPORT_IGNORE_FIELD,
  parseImportInput,
  type ImportCellValidation,
  type ManagementTableRow,
  type ManagementTableSectionId,
  type ManagementTableStore,
  type ParsedImportBatch,
} from './management-table-import';
import {
  createAdminSettingsResource,
  deleteAdminSettingsResource,
  fetchAdminSettingsOverview,
  fetchAdminSettingsResource,
  reorderAdminSettingsResource,
  type AdminSettingsLocationRecord,
  type AdminSettingsOverviewResponse,
  type AdminSettingsRecordMap,
  type AdminSettingsResource,
  type AdminSettingsSchedulerAdminScopeRecord,
  type AdminSettingsScheduleTypeRecord,
  type AdminSettingsServiceLineRecord,
  type AdminSettingsTeamRecord,
  updateAdminSettingsResource,
  updateAdminSettingsSection,
} from './api';

type ManagementTableColumnType = 'text' | 'email' | 'status' | 'color' | 'date';

type ManagementTableColumn = {
  key: string;
  label: string;
  type: ManagementTableColumnType;
  sortable?: boolean;
  editable?: boolean;
  bulkEditable?: boolean;
  width?: string;
};

type ManagementTableSectionConfig = {
  id: ManagementTableSectionId;
  checklistKey: OnboardingSectionKey;
  title: string;
  addLabel: string;
  importLabel: string;
  reorderLabel: string;
  orderMatters: boolean;
  deleteLabel: string;
  deactivateLabel: string;
  spreadsheetHint: string;
  saveHint: string;
  destructiveHint: string;
  defaultStatus: string;
  statusOptions: string[];
  filters: Array<{ label: string; value: string }>;
  columns: ManagementTableColumn[];
  rows: ManagementTableRow[];
};

type ManagementTableScaffoldProps = {
  sectionId: ManagementTableSectionId;
  tenantQuery: string;
  onSyncChecklist: (sectionKey: OnboardingSectionKey, patch: OnboardingChecklistSectionSync) => void;
};

type RowModalState =
  | { mode: 'add' }
  | { mode: 'edit'; rowId: string }
  | null;

type ConfirmModalState =
  | { mode: 'delete' | 'deactivate'; rowIds: string[] }
  | null;

type ApplyValueModalState =
  | { mode: 'apply-selected' }
  | { mode: 'copy-down' }
  | null;

type ColorSource = 'manual' | 'inherited' | 'mixed' | 'default';

type ColorMeta = {
  hex: string | null;
  source: ColorSource;
  sourceLabel: string;
  accessibilityLabel: string;
  accessibilityTone: 'success' | 'warning';
  recommendedTextColor: '#111827' | '#ffffff';
};

export const MANAGEMENT_TABLE_STORAGE_KEY = 'caresCompanyAdminManagementTablesScaffold';
export const MANAGEMENT_TABLE_UPDATED_EVENT = 'cares:admin-settings-management-store-updated';

const now = () => new Date().toISOString();

const formatSavedAt = (value: string) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

const createRowId = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

const sortRowsByOrder = (rows: ManagementTableRow[]) =>
  [...rows].sort((left, right) => left.sortOrder - right.sortOrder);

const createDefaultRows = (
  prefix: string,
  rows: Array<Omit<ManagementTableRow, 'id' | 'sortOrder' | 'lastUpdated'>>
) =>
  rows.map((row, index) => ({
    ...row,
    id: createRowId(`${prefix}-${index + 1}`),
    sortOrder: index,
    lastUpdated: now(),
  }));

const normalizeText = (value: string) => value.trim().toLowerCase();

const splitScopeValues = (value: string) =>
  value
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);

const colorHexRegex = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const colorPresets = [
  { label: 'Blue', value: '#0d6efd' },
  { label: 'Green', value: '#198754' },
  { label: 'Teal', value: '#20c997' },
  { label: 'Cyan', value: '#0dcaf0' },
  { label: 'Orange', value: '#fd7e14' },
  { label: 'Red', value: '#dc3545' },
  { label: 'Purple', value: '#6f42c1' },
  { label: 'Slate', value: '#6c757d' },
];

const expandHexColor = (value: string) => {
  const normalized = value.trim();

  if (!colorHexRegex.test(normalized)) {
    return null;
  }

  if (normalized.length === 4) {
    const [, red, green, blue] = normalized;
    return `#${red}${red}${green}${green}${blue}${blue}`.toUpperCase();
  }

  return normalized.toUpperCase();
};

const getChannelLuminance = (channel: number) => {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

const getContrastRatio = (leftHex: string, rightHex: string) => {
  const toRgb = (hex: string) => {
    const normalized = expandHexColor(hex);

    if (!normalized) {
      return null;
    }

    return {
      red: Number.parseInt(normalized.slice(1, 3), 16),
      green: Number.parseInt(normalized.slice(3, 5), 16),
      blue: Number.parseInt(normalized.slice(5, 7), 16),
    };
  };

  const left = toRgb(leftHex);
  const right = toRgb(rightHex);

  if (!left || !right) {
    return 1;
  }

  const getRelativeLuminance = ({ red, green, blue }: { red: number; green: number; blue: number }) =>
    (0.2126 * getChannelLuminance(red))
    + (0.7152 * getChannelLuminance(green))
    + (0.0722 * getChannelLuminance(blue));

  const leftLuminance = getRelativeLuminance(left);
  const rightLuminance = getRelativeLuminance(right);
  const lighter = Math.max(leftLuminance, rightLuminance);
  const darker = Math.min(leftLuminance, rightLuminance);

  return (lighter + 0.05) / (darker + 0.05);
};

const getColorMeta = (
  sectionId: ManagementTableSectionId,
  row: Pick<ManagementTableRow, 'cells'>,
  store: ManagementTableStore
): ColorMeta => {
  const rawHex = row.cells.color ?? '';
  const manualHex = expandHexColor(rawHex);

  const buildMeta = (hex: string | null, source: ColorSource, sourceLabel: string): ColorMeta => {
    if (!hex) {
      return {
        hex: null,
        source,
        sourceLabel,
        accessibilityLabel: 'No color set; labels and status badges remain the primary signal.',
        accessibilityTone: 'warning',
        recommendedTextColor: '#111827',
      };
    }

    const whiteContrast = getContrastRatio(hex, '#FFFFFF');
    const darkContrast = getContrastRatio(hex, '#111827');
    const recommendedTextColor = whiteContrast >= darkContrast ? '#ffffff' : '#111827';
    const strongestContrast = Math.max(whiteContrast, darkContrast);

    return {
      hex,
      source,
      sourceLabel,
      accessibilityLabel: strongestContrast >= 4.5
        ? `Contrast check passes with ${recommendedTextColor === '#ffffff' ? 'white' : 'dark'} text.`
        : 'Low contrast warning: choose a darker or lighter color so labels stay readable.',
      accessibilityTone: strongestContrast >= 4.5 ? 'success' : 'warning',
      recommendedTextColor,
    };
  };

  if (sectionId === 'locations' || sectionId === 'schedule-types') {
    return buildMeta(manualHex, manualHex ? 'manual' : 'default', manualHex ? 'Manual color' : 'Default styling');
  }

  if (sectionId === 'service-lines') {
    if (manualHex) {
      return buildMeta(manualHex, 'manual', row.cells.locations?.trim() === 'Companywide' ? 'Manual companywide color' : 'Manual color');
    }

    const locationNames = splitScopeValues(row.cells.locations ?? '');
    const matchingLocationColors = Array.from(
      new Set(
        store.locations
          .filter((location) => locationNames.some((name) => normalizeText(location.cells.name ?? '') === normalizeText(name)))
          .map((location) => expandHexColor(location.cells.color ?? ''))
          .filter((value): value is string => Boolean(value))
      )
    );

    if (matchingLocationColors.length === 1) {
      return buildMeta(matchingLocationColors[0], 'inherited', 'Inherited from location');
    }

    if (matchingLocationColors.length > 1) {
      return buildMeta(null, 'mixed', 'Mixed location colors');
    }

    return buildMeta(null, 'default', 'Default styling');
  }

  if (sectionId === 'groups-teams') {
    if (manualHex) {
      return buildMeta(manualHex, 'manual', 'Manual team color');
    }

    const serviceLineName = row.cells.serviceLine ?? '';
    const matchingServiceLine = store['service-lines'].find((serviceLine) =>
      normalizeText(serviceLine.cells.name ?? '') === normalizeText(serviceLineName)
    );

    if (matchingServiceLine) {
      const inherited = getColorMeta('service-lines', matchingServiceLine, store);
      return buildMeta(inherited.hex, inherited.hex ? 'inherited' : inherited.source, inherited.hex ? 'Inherited from service line' : inherited.sourceLabel);
    }
  }

  return buildMeta(manualHex, manualHex ? 'manual' : 'default', manualHex ? 'Manual color' : 'Default styling');
};

const schedulerAdminDirectory = [
  { name: 'Dr. Jane Smith', email: 'jane.smith@stlukes.org', role: 'Existing CARES user', note: 'Trauma schedule lead already has a tenant account.' },
  { name: 'Dr. Alex Patel', email: 'alex.patel@stlukes.org', role: 'Existing CARES user', note: 'Already active in the company tenant.' },
  { name: 'Dr. Riley Chen', email: 'riley.chen@stlukes.org', role: 'Existing CARES user', note: 'Available for reassignment without creating a duplicate user.' },
];

const findDirectoryUser = (email: string) =>
  schedulerAdminDirectory.find((entry) => normalizeText(entry.email) === normalizeText(email));

const toSchedulerAdminStatus = (status: string) => {
  switch (normalizeText(status)) {
    case 'invited':
    case 'inactive':
      return status === 'inactive' ? 'deactivated' : 'pending';
    default:
      return status || 'pending';
  }
};

const buildCanonicalSchedulerAdminRows = (overview: AdminSettingsOverviewResponse): ManagementTableRow[] => {
  type SchedulerAdminAccumulator = {
    id: number;
    name: string;
    email: string | null;
    statuses: Set<string>;
    departments: Set<string>;
    directGroups: Set<string>;
    inheritedGroups: Set<string>;
  };

  const administrators = new Map<number, SchedulerAdminAccumulator>();
  const ensureAdministrator = (admin: { id: number; name: string; email: string | null; status: string }) => {
    const current = administrators.get(admin.id) ?? {
      id: admin.id,
      name: admin.name,
      email: admin.email,
      statuses: new Set<string>(),
      departments: new Set<string>(),
      directGroups: new Set<string>(),
      inheritedGroups: new Set<string>(),
    };
    current.statuses.add(admin.status);
    administrators.set(admin.id, current);
    return current;
  };

  overview.department_hierarchy.forEach((department) => {
    department.scheduler_admins.forEach((admin) => {
      const current = ensureAdministrator(admin);
      current.departments.add(department.name);
      department.schedule_groups.forEach((group) => current.inheritedGroups.add(group.name));
    });

    department.schedule_groups.forEach((group) => {
      group.direct_scheduler_admins.forEach((admin) => {
        ensureAdministrator(admin).directGroups.add(group.name);
      });
    });
  });

  overview.unassigned_schedule_groups.forEach((group) => {
    group.direct_scheduler_admins.forEach((admin) => {
      ensureAdministrator(admin).directGroups.add(group.name);
    });
  });

  return [...administrators.values()]
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }))
    .map((admin, index) => {
      const status = admin.statuses.has('active') ? 'active' : [...admin.statuses][0] ?? 'pending';
      const effectiveGroups = new Set([...admin.directGroups, ...admin.inheritedGroups]);

      return {
        id: `canonical-user-${admin.id}`,
        status,
        sortOrder: index,
        lastUpdated: '',
        dependencyWarning: null,
        cells: {
          name: admin.name,
          email: admin.email ?? '',
          scheduleType: 'Company Department / Schedule Group access',
          serviceLine: [...admin.departments].sort().join('; '),
          locationScope: [...admin.inheritedGroups].sort().join('; '),
          teamScope: [...admin.directGroups].sort().join('; '),
          inviteStatus: [...effectiveGroups].sort().join('; '),
          comments: '',
          status,
          assignmentKind: 'existing_user',
          recordSource: 'canonical_group_roles',
          inviteSentAt: '',
          activatedAt: '',
          deactivatedAt: '',
        },
      };
    });
};

const getSchedulerAdminAssignmentKind = (row: ManagementTableRow) =>
  row.cells.assignmentKind?.trim() || (findDirectoryUser(row.cells.email ?? '') ? 'existing_user' : 'new_invite');

const getSchedulerAdminRowStatus = (row: ManagementTableRow) => toSchedulerAdminStatus(row.status);

const isCoverageEligibleForActivation = (row: ManagementTableRow) => {
  const status = getSchedulerAdminRowStatus(row);
  return status === 'pending' || status === 'active';
};

const isCoverageEligibleForFirstDraft = (row: ManagementTableRow) => getSchedulerAdminRowStatus(row) === 'active';

const isManagedServiceLine = (row: ManagementTableRow) => {
  const status = normalizeText(row.status);
  return status === 'active' || status === 'draft';
};

export type SchedulerAdminCoverageSummary = {
  assignmentCount: number;
  pendingCount: number;
  activeCount: number;
  deactivatedCount: number;
  expiredCount: number;
  companyActivationCoveredCount: number;
  firstDraftReadyCount: number;
  activeServiceLineCount: number;
  uncoveredForActivation: string[];
  uncoveredForFirstDraft: string[];
};

export const buildSchedulerAdminCoverageSummary = (store: ManagementTableStore): SchedulerAdminCoverageSummary => {
  const serviceLines = store['service-lines'].filter(isManagedServiceLine);
  const assignments = store['scheduler-admins'].map((row) => ({
    row,
    status: getSchedulerAdminRowStatus(row),
    serviceLine: row.cells.serviceLine?.trim() ?? '',
  }));

  const hasCoverage = (serviceLine: string, predicate: (row: ManagementTableRow) => boolean) =>
    assignments.some((entry) => normalizeText(entry.serviceLine) === normalizeText(serviceLine) && predicate(entry.row));

  const uncoveredForActivation = serviceLines
    .filter((row) => !hasCoverage(row.cells.name ?? '', isCoverageEligibleForActivation))
    .map((row) => row.cells.name ?? 'Unknown service line');

  const uncoveredForFirstDraft = serviceLines
    .filter((row) => !hasCoverage(row.cells.name ?? '', isCoverageEligibleForFirstDraft))
    .map((row) => row.cells.name ?? 'Unknown service line');

  return {
    assignmentCount: assignments.length,
    pendingCount: assignments.filter((entry) => entry.status === 'pending').length,
    activeCount: assignments.filter((entry) => entry.status === 'active').length,
    deactivatedCount: assignments.filter((entry) => entry.status === 'deactivated').length,
    expiredCount: assignments.filter((entry) => entry.status === 'expired').length,
    companyActivationCoveredCount: serviceLines.length - uncoveredForActivation.length,
    firstDraftReadyCount: serviceLines.length - uncoveredForFirstDraft.length,
    activeServiceLineCount: serviceLines.length,
    uncoveredForActivation,
    uncoveredForFirstDraft,
  };
};

const buildSchedulerAdminRowWarning = (row: ManagementTableRow, store: ManagementTableStore) => {
  const serviceLine = row.cells.serviceLine?.trim() ?? '';
  const status = getSchedulerAdminRowStatus(row);
  const siblingRows = store['scheduler-admins'].filter((candidate) =>
    candidate.id !== row.id && normalizeText(candidate.cells.serviceLine ?? '') === normalizeText(serviceLine)
  );

  if (status === 'pending') {
    return 'Pending invite counts for company activation coverage, but this service line still needs an active Scheduler Admin for first-draft readiness.';
  }

  if (status === 'deactivated' || status === 'expired') {
    const stillCoveredForActivation = siblingRows.some(isCoverageEligibleForActivation);
    const stillCoveredForFirstDraft = siblingRows.some(isCoverageEligibleForFirstDraft);

    if (!stillCoveredForActivation) {
      return `${serviceLine || 'This service line'} no longer has Company activation coverage after this deactivation.`;
    }

    if (!stillCoveredForFirstDraft) {
      return `${serviceLine || 'This service line'} still has invite coverage, but it no longer has an active Scheduler Admin for first-draft readiness.`;
    }
  }

  return row.dependencyWarning;
};

const hydrateSchedulerAdminRow = (row: ManagementTableRow): ManagementTableRow => {
  const existingUser = findDirectoryUser(row.cells.email ?? '');
  const status = toSchedulerAdminStatus(row.status);

  return {
    ...row,
    status,
    cells: {
      ...row.cells,
      status,
      scheduleType: row.cells.scheduleType ?? 'Clinician',
      inviteStatus: row.cells.inviteStatus ?? (status === 'active' ? 'Activated' : status === 'deactivated' ? 'Deactivated' : 'Invite sent'),
      assignmentKind: row.cells.assignmentKind ?? (existingUser ? 'existing_user' : 'new_invite'),
      inviteSentAt: row.cells.inviteSentAt ?? (status === 'pending' ? row.lastUpdated : ''),
      activatedAt: row.cells.activatedAt ?? (status === 'active' ? row.lastUpdated : ''),
      deactivatedAt: row.cells.deactivatedAt ?? (status === 'deactivated' ? row.lastUpdated : ''),
    },
  };
};

const sectionConfigs: Record<ManagementTableSectionId, ManagementTableSectionConfig> = {
  locations: {
    id: 'locations',
    checklistKey: 'locations',
    title: 'Hospitals / Locations',
    addLabel: 'Add location',
    importLabel: 'Import / paste locations',
    reorderLabel: 'Display order',
    orderMatters: true,
    deleteLabel: 'Delete selected locations',
    deactivateLabel: 'Deactivate selected locations',
    spreadsheetHint: 'Open the import modal to review format guidance, paste spreadsheet rows or newline lists, map columns, validate, and preview rows before commit.',
    saveHint: 'Save section status to refresh readiness metadata while row changes continue to save through the Company Admin Settings APIs for the active company.',
    destructiveHint: 'Locations already used by service lines or schedules should normally be deactivated instead of hard-deleted.',
    defaultStatus: 'active',
    statusOptions: ['active', 'inactive', 'draft', 'archived'],
    filters: [
      { label: 'All statuses', value: 'all' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
      { label: 'With dependency warning', value: 'warning' },
    ],
    columns: [
      { key: 'name', label: 'Name', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '18%' },
      { key: 'code', label: 'Code', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '9%' },
      { key: 'region', label: 'Region', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '16%' },
      { key: 'serviceLines', label: 'Service lines', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '20%' },
      { key: 'color', label: 'Color', type: 'color', sortable: true, editable: true, bulkEditable: true, width: '10%' },
      { key: 'status', label: 'Status', type: 'status', sortable: true, editable: true, bulkEditable: true, width: '10%' },
      { key: 'lastUpdated', label: 'Last updated', type: 'date', sortable: true, width: '12%' },
    ],
    rows: createDefaultRows('location', [
      {
        status: 'active',
        dependencyWarning: 'Trauma and Acute Care Surgery currently reference this site in readiness review.',
        cells: {
          name: "St. Luke's Main Hospital",
          code: 'MAIN',
          region: 'Bethlehem, PA',
          serviceLines: 'Trauma; Acute Care Surgery; General Surgery',
          color: '#0d6efd',
          status: 'active',
        },
      },
      {
        status: 'active',
        dependencyWarning: null,
        cells: {
          name: "St. Luke's East",
          code: 'EAST',
          region: 'Easton, PA',
          serviceLines: 'Trauma; General Surgery',
          color: '#198754',
          status: 'active',
        },
      },
      {
        status: 'draft',
        dependencyWarning: 'Address or region still needs review before this row should be considered complete.',
        cells: {
          name: "St. Luke's West",
          code: 'WEST',
          region: '',
          serviceLines: 'ICU / Critical Care',
          color: '#fd7e14',
          status: 'draft',
        },
      },
    ]),
  },
  'schedule-types': {
    id: 'schedule-types',
    checklistKey: 'schedule_types',
    title: 'Schedule Types',
    addLabel: 'Add schedule type',
    importLabel: 'Import / paste schedule types',
    reorderLabel: 'Type order',
    orderMatters: true,
    deleteLabel: 'Delete selected schedule types',
    deactivateLabel: 'Deactivate selected schedule types',
    spreadsheetHint: 'Use the import modal to seed type hierarchies with alias mapping, validation, example templates, and local preview before commit.',
    saveHint: 'Save section status to refresh readiness metadata after schedule type rows are updated.',
    destructiveHint: 'Changing or deleting a schedule type can cascade into service-line mapping, so this pass only previews those impacts.',
    defaultStatus: 'active',
    statusOptions: ['active', 'inactive', 'draft'],
    filters: [
      { label: 'All statuses', value: 'all' },
      { label: 'Active', value: 'active' },
      { label: 'Draft', value: 'draft' },
      { label: 'With dependency warning', value: 'warning' },
    ],
    columns: [
      { key: 'name', label: 'Name', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '22%' },
      { key: 'code', label: 'Code', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '10%' },
      { key: 'description', label: 'Description', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '24%' },
      { key: 'serviceLineCoverage', label: 'Service line coverage', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '20%' },
      { key: 'color', label: 'Color', type: 'color', sortable: true, editable: true, bulkEditable: true, width: '10%' },
      { key: 'status', label: 'Status', type: 'status', sortable: true, editable: true, bulkEditable: true, width: '10%' },
      { key: 'lastUpdated', label: 'Last updated', type: 'date', sortable: true, width: '12%' },
    ],
    rows: createDefaultRows('schedule-type', [
      {
        status: 'active',
        dependencyWarning: null,
        cells: {
          name: 'Clinician',
          code: 'CLIN',
          description: 'Physician and APP schedule families.',
          serviceLineCoverage: 'Trauma; Acute Care Surgery; General Surgery',
          color: '#6610f2',
          status: 'active',
        },
      },
      {
        status: 'active',
        dependencyWarning: null,
        cells: {
          name: 'Nursing',
          code: 'RN',
          description: 'Nursing staffing schedule families.',
          serviceLineCoverage: 'ICU Nursing',
          color: '#198754',
          status: 'active',
        },
      },
      {
        status: 'draft',
        dependencyWarning: 'Ancillary naming still needs review before downstream imports should rely on it.',
        cells: {
          name: 'Ancillary',
          code: 'ANC',
          description: 'Respiratory therapy and other support services.',
          serviceLineCoverage: 'Respiratory Therapy',
          color: '#0dcaf0',
          status: 'draft',
        },
      },
    ]),
  },
  'service-lines': {
    id: 'service-lines',
    checklistKey: 'service_lines',
    title: 'Service Lines',
    addLabel: 'Add service line',
    importLabel: 'Import / paste service lines',
    reorderLabel: 'Service-line order',
    orderMatters: true,
    deleteLabel: 'Delete selected service lines',
    deactivateLabel: 'Deactivate selected service lines',
    spreadsheetHint: 'Use the import modal for schedule-type and location validation, Companywide handling, preview correction, and saving into the current draft.',
    saveHint: 'Save section status to refresh readiness metadata so missing coverage continues to surface as a blocker.',
    destructiveHint: 'Service lines sit in the middle of the schedule hierarchy, so scope-changing edits show warnings before the change is applied.',
    defaultStatus: 'active',
    statusOptions: ['active', 'inactive', 'draft'],
    filters: [
      { label: 'All statuses', value: 'all' },
      { label: 'Active', value: 'active' },
      { label: 'Draft', value: 'draft' },
      { label: 'With dependency warning', value: 'warning' },
    ],
    columns: [
      { key: 'name', label: 'Name', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '18%' },
      { key: 'code', label: 'Code', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '9%' },
      { key: 'scheduleType', label: 'Schedule type', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '14%' },
      { key: 'locations', label: 'Locations', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '19%' },
      { key: 'schedulerAdmin', label: 'Scheduler Admin', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '18%' },
      { key: 'color', label: 'Color', type: 'color', sortable: true, editable: true, bulkEditable: true, width: '8%' },
      { key: 'status', label: 'Status', type: 'status', sortable: true, editable: true, bulkEditable: true, width: '10%' },
      { key: 'lastUpdated', label: 'Last updated', type: 'date', sortable: true, width: '12%' },
    ],
    rows: createDefaultRows('service-line', [
      {
        status: 'active',
        dependencyWarning: null,
        cells: {
          name: 'Trauma',
          code: 'TRAUMA',
          scheduleType: 'Clinician',
          locations: "St. Luke's Main Hospital; St. Luke's East",
          schedulerAdmin: 'Dr. Jane Smith',
          color: '#dc3545',
          status: 'active',
        },
      },
      {
        status: 'active',
        dependencyWarning: null,
        cells: {
          name: 'Acute Care Surgery',
          code: 'ACS',
          scheduleType: 'Clinician',
          locations: "St. Luke's Main Hospital",
          schedulerAdmin: 'Dr. Alex Patel',
          color: '#fd7e14',
          status: 'active',
        },
      },
      {
        status: 'draft',
        dependencyWarning: 'General Surgery is missing a location assignment or explicit Companywide designation.',
        cells: {
          name: 'General Surgery',
          code: 'GS',
          scheduleType: 'Clinician',
          locations: '',
          schedulerAdmin: '',
          color: '#20c997',
          status: 'draft',
        },
      },
    ]),
  },
  'groups-teams': {
    id: 'groups-teams',
    checklistKey: 'groups_teams',
    title: 'Departments',
    addLabel: 'Add team',
    importLabel: 'Import / paste teams',
    reorderLabel: 'Team order',
    orderMatters: true,
    deleteLabel: 'Delete selected teams',
    deactivateLabel: 'Deactivate selected teams',
    spreadsheetHint: 'Teams use the same import preview, parent service-line validation, and inline correction flow as the required setup sections.',
    saveHint: 'If no rows are needed, this section can still be marked complete in backend section status. Local team rows remain optional.',
    destructiveHint: 'Team deletes should preview downstream schedule or scope impact before hard-delete is allowed.',
    defaultStatus: 'active',
    statusOptions: ['active', 'inactive', 'draft'],
    filters: [
      { label: 'All statuses', value: 'all' },
      { label: 'Active', value: 'active' },
      { label: 'Draft', value: 'draft' },
      { label: 'With dependency warning', value: 'warning' },
    ],
    columns: [
      { key: 'name', label: 'Name', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '20%' },
      { key: 'serviceLine', label: 'Parent service line', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '18%' },
      { key: 'location', label: 'Location', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '18%' },
      { key: 'schedulerAdmin', label: 'Scheduler Admin', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '18%' },
      { key: 'color', label: 'Color', type: 'color', sortable: true, editable: true, bulkEditable: true, width: '8%' },
      { key: 'status', label: 'Status', type: 'status', sortable: true, editable: true, bulkEditable: true, width: '10%' },
      { key: 'lastUpdated', label: 'Last updated', type: 'date', sortable: true, width: '12%' },
    ],
    rows: createDefaultRows('team', [
      {
        status: 'active',
        dependencyWarning: null,
        cells: {
          name: 'Trauma Attendings',
          serviceLine: 'Trauma',
          location: "St. Luke's Main Hospital",
          schedulerAdmin: 'Dr. Jane Smith',
          color: '#6f42c1',
          status: 'active',
        },
      },
      {
        status: 'active',
        dependencyWarning: 'This scope should be reviewed before moving coverage away from Trauma.',
        cells: {
          name: 'Trauma Residents/APPs',
          serviceLine: 'Trauma',
          location: "St. Luke's Main Hospital",
          schedulerAdmin: 'Dr. Jane Smith',
          color: '#0dcaf0',
          status: 'active',
        },
      },
    ]),
  },
  'scheduler-admins': {
    id: 'scheduler-admins',
    checklistKey: 'scheduler_admins',
    title: 'Scheduler Admin Assignments',
    addLabel: 'Add Scheduler Admin',
    importLabel: 'Import / paste assignments',
    reorderLabel: 'Coverage order',
    orderMatters: false,
    deleteLabel: 'Delete selected assignments',
    deactivateLabel: 'Deactivate selected assignments',
    spreadsheetHint: 'The import modal validates email, schedule-type scope, service-line scope, location/team references, and link-versus-invite behavior before rows enter the current assignment draft.',
    saveHint: 'Save section status to refresh readiness metadata for pending, active, and deactivated coverage.',
    destructiveHint: 'Removing a Scheduler Admin should preview service lines left without coverage before the action is allowed.',
    defaultStatus: 'pending',
    statusOptions: ['pending', 'active', 'deactivated', 'expired'],
    filters: [
      { label: 'All statuses', value: 'all' },
      { label: 'Pending', value: 'pending' },
      { label: 'Active', value: 'active' },
      { label: 'Deactivated', value: 'deactivated' },
      { label: 'With dependency warning', value: 'warning' },
    ],
    columns: [
      { key: 'name', label: 'Name', type: 'text', sortable: true, editable: true, bulkEditable: true, width: '16%' },
      { key: 'email', label: 'Email', type: 'email', sortable: true, editable: true, bulkEditable: true, width: '19%' },
      { key: 'serviceLine', label: 'Department', type: 'text', sortable: true, width: '18%' },
      { key: 'teamScope', label: 'Direct Groups', type: 'text', sortable: true, width: '18%' },
      { key: 'status', label: 'Status', type: 'status', sortable: true, width: '10%' },
      { key: 'comments', label: 'Comments', type: 'text', sortable: true, width: '19%' },
    ],
    rows: createDefaultRows('scheduler-admin', [
      {
        status: 'pending',
        dependencyWarning: 'Trauma readiness remains blocked until this invite is accepted or coverage is reassigned.',
        cells: {
          name: 'Dr. Jane Smith',
          email: 'jane.smith@stlukes.org',
          scheduleType: 'Clinician',
          serviceLine: 'Trauma',
          locationScope: "St. Luke's Main Hospital; St. Luke's East",
          teamScope: 'Trauma Attendings; Trauma Residents/APPs',
          inviteStatus: 'Invite sent',
          status: 'pending',
          assignmentKind: 'existing_user',
          inviteSentAt: '2026-05-09T15:31:00-04:00',
          activatedAt: '',
          deactivatedAt: '',
        },
      },
      {
        status: 'active',
        dependencyWarning: null,
        cells: {
          name: 'Dr. Alex Patel',
          email: 'alex.patel@stlukes.org',
          scheduleType: 'Clinician',
          serviceLine: 'Acute Care Surgery',
          locationScope: "St. Luke's Main Hospital",
          teamScope: 'All',
          inviteStatus: 'Activated',
          status: 'active',
          assignmentKind: 'existing_user',
          inviteSentAt: '2026-05-08T13:05:00-04:00',
          activatedAt: '2026-05-08T15:22:00-04:00',
          deactivatedAt: '',
        },
      },
      {
        status: 'deactivated',
        dependencyWarning: 'Reactivating or re-inviting this row is the fastest way to restore General Surgery coverage.',
        cells: {
          name: 'Dr. Riley Chen',
          email: 'riley.chen@stlukes.org',
          scheduleType: 'Clinician',
          serviceLine: 'General Surgery',
          locationScope: 'Companywide',
          teamScope: 'All',
          inviteStatus: 'Deactivated',
          status: 'deactivated',
          assignmentKind: 'existing_user',
          inviteSentAt: '2026-05-06T10:15:00-04:00',
          activatedAt: '2026-05-06T11:00:00-04:00',
          deactivatedAt: '2026-05-09T09:05:00-04:00',
        },
      },
    ]),
  },
};

const createDefaultStore = (): ManagementTableStore => ({
  locations: sectionConfigs.locations.rows,
  'schedule-types': sectionConfigs['schedule-types'].rows,
  'service-lines': sectionConfigs['service-lines'].rows,
  'groups-teams': sectionConfigs['groups-teams'].rows,
  'scheduler-admins': sectionConfigs['scheduler-admins'].rows,
});

const hydrateStore = (store: ManagementTableStore): ManagementTableStore => ({
  ...store,
  'scheduler-admins': store['scheduler-admins'].map(hydrateSchedulerAdminRow),
});

let cachedManagementTableStore = hydrateStore(createDefaultStore());

export const loadManagementTableStore = (): ManagementTableStore => {
  return cachedManagementTableStore;
};

const persistStore = (store: ManagementTableStore) => {
  cachedManagementTableStore = hydrateStore(store);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(MANAGEMENT_TABLE_UPDATED_EVENT));
  }
};

export const getSchedulerAdminCoverageSummary = () =>
  buildSchedulerAdminCoverageSummary(loadManagementTableStore());

export type ResourceLookupState = {
  locations: AdminSettingsLocationRecord[];
  'schedule-types': AdminSettingsScheduleTypeRecord[];
  'service-lines': AdminSettingsServiceLineRecord[];
  teams: AdminSettingsTeamRecord[];
  'scheduler-admin-scopes': AdminSettingsSchedulerAdminScopeRecord[];
};

export const emptyLookupState = (): ResourceLookupState => ({
  locations: [],
  'schedule-types': [],
  'service-lines': [],
  teams: [],
  'scheduler-admin-scopes': [],
});

const sectionIdToResource = (sectionId: ManagementTableSectionId): Exclude<AdminSettingsResource, 'schedules'> => {
  switch (sectionId) {
    case 'locations':
      return 'locations';
    case 'schedule-types':
      return 'schedule-types';
    case 'service-lines':
      return 'service-lines';
    case 'groups-teams':
      return 'teams';
    case 'scheduler-admins':
      return 'scheduler-admin-scopes';
  }
};

const resourceToStoreSectionId = (resource: Exclude<AdminSettingsResource, 'schedules'>): ManagementTableSectionId => {
  switch (resource) {
    case 'locations':
      return 'locations';
    case 'schedule-types':
      return 'schedule-types';
    case 'service-lines':
      return 'service-lines';
    case 'teams':
      return 'groups-teams';
    case 'scheduler-admin-scopes':
      return 'scheduler-admins';
  }
};

const normalizeLookupValue = (value: string | null | undefined) => normalizeText(value ?? '');

const findByNameOrCode = <TRecord extends { id: number; name?: string | null; code?: string | null }>(
  records: TRecord[],
  rawValue: string
) => records.find((record) =>
  normalizeLookupValue(record.name) === normalizeLookupValue(rawValue)
  || normalizeLookupValue(record.code) === normalizeLookupValue(rawValue)
);

const joinNonEmpty = (values: Array<string | null | undefined>, fallback = '') => {
  const joined = values.filter(Boolean).join(', ');
  return joined || fallback;
};

const formatScopeStatus = (scope: AdminSettingsSchedulerAdminScopeRecord) => {
  switch (scope.handoff_status) {
    case 'active':
    case 'complete':
      return 'active';
    case 'invited':
    case 'pending_acceptance':
      return 'pending';
    default:
      return scope.status === 'inactive' ? 'deactivated' : scope.status;
  }
};

export const toManagementRow = (
  sectionId: ManagementTableSectionId,
  record: AdminSettingsRecordMap[Exclude<AdminSettingsResource, 'schedules'>],
  lookups: ResourceLookupState
): ManagementTableRow => {
  const lastUpdated = record.updated_at ?? record.created_at ?? now();

  switch (sectionId) {
    case 'locations': {
      const item = record as AdminSettingsLocationRecord;
      const relatedServiceLines = lookups['service-lines']
        .filter((serviceLine) => serviceLine.locations?.some((location) => location.id === item.id))
        .map((serviceLine) => serviceLine.name);

      return {
        id: String(item.id),
        status: item.status,
        sortOrder: item.sort_order,
        lastUpdated,
        dependencyWarning: null,
        cells: {
          name: item.name,
          code: item.code ?? '',
          region: joinNonEmpty([item.city, item.state_region], ''),
          serviceLines: relatedServiceLines.join('; '),
          color: item.color_hex ?? '',
          status: item.status,
        },
      };
    }
    case 'schedule-types': {
      const item = record as AdminSettingsScheduleTypeRecord;
      const coverage = lookups['service-lines']
        .filter((serviceLine) => serviceLine.schedule_type_id === item.id)
        .map((serviceLine) => serviceLine.name);

      return {
        id: String(item.id),
        status: item.status,
        sortOrder: item.sort_order,
        lastUpdated,
        dependencyWarning: null,
        cells: {
          name: item.name,
          code: item.code,
          description: item.description ?? '',
          serviceLineCoverage: coverage.join('; '),
          color: item.color_hex ?? '',
          status: item.status,
        },
      };
    }
    case 'service-lines': {
      const item = record as AdminSettingsServiceLineRecord;
      const schedulerAdmin = lookups['scheduler-admin-scopes']
        .flatMap((scope) => scope.scope_users?.map((scopeUser) => ({
          serviceLineId: scope.service_line_id,
          fullName: scopeUser.full_name,
        })) ?? [])
        .find((scopeUser) => scopeUser.serviceLineId === item.id);

      return {
        id: String(item.id),
        status: item.status,
        sortOrder: item.sort_order,
        lastUpdated,
        dependencyWarning: item.coverage_mode === 'location_bound' && (item.locations?.length ?? 0) === 0
          ? `${item.name} is missing a location assignment or explicit Companywide designation.`
          : null,
        cells: {
          name: item.name,
          code: item.code ?? '',
          scheduleType: item.schedule_type?.name ?? '',
          locations: item.coverage_mode === 'companywide'
            ? 'Companywide'
            : (item.locations ?? []).map((location) => location.name).join('; '),
          schedulerAdmin: schedulerAdmin?.fullName ?? '',
          color: item.color_hex ?? '',
          status: item.status,
        },
      };
    }
    case 'groups-teams': {
      const item = record as AdminSettingsTeamRecord;
      const scope = lookups['scheduler-admin-scopes'].find((candidate) => candidate.team_id === item.id);

      return {
        id: String(item.id),
        status: item.status,
        sortOrder: item.sort_order,
        lastUpdated,
        dependencyWarning: null,
        cells: {
          name: item.name,
          serviceLine: item.service_line?.name ?? '',
          location: (item.locations ?? []).map((location) => location.name).join('; '),
          schedulerAdmin: scope?.scope_users?.[0]?.full_name ?? '',
          color: item.color_hex ?? '',
          status: item.status,
        },
      };
    }
    case 'scheduler-admins': {
      const item = record as AdminSettingsSchedulerAdminScopeRecord;
      const primaryUser = item.scope_users?.find((scopeUser) => scopeUser.is_primary) ?? item.scope_users?.[0];
      const scheduleType = item.schedule_type?.name ?? '';
      const serviceLine = item.service_line?.name ?? '';
      const locationScope = item.location?.name ?? '';
      const teamScope = item.team?.name ?? 'All';
      const rowStatus = formatScopeStatus(item);

      return hydrateSchedulerAdminRow({
        id: String(item.id),
        status: rowStatus,
        sortOrder: 0,
        lastUpdated,
        dependencyWarning: null,
        cells: {
          name: primaryUser?.full_name ?? '',
          email: primaryUser?.invite_email ?? '',
          scheduleType,
          serviceLine,
          locationScope,
          teamScope,
          inviteStatus: item.handoff_status.replace(/_/g, ' '),
          comments: item.notes ?? '',
          status: rowStatus,
          assignmentKind: primaryUser?.user_id ? 'existing_user' : 'new_invite',
          inviteSentAt: primaryUser?.invite_sent_at ?? '',
          activatedAt: primaryUser?.activated_at ?? '',
          deactivatedAt: primaryUser?.deactivated_at ?? '',
        },
      });
    }
  }
};

const buildPayloadFromRow = (
  sectionId: ManagementTableSectionId,
  row: ManagementTableRow,
  lookups: ResourceLookupState,
  existingRecordId?: number
): Record<string, unknown> => {
  switch (sectionId) {
    case 'locations':
      return {
        name: row.cells.name?.trim(),
        code: row.cells.code?.trim() || null,
        city: row.cells.region?.trim() || null,
        state_region: null,
        address_line_1: null,
        address_line_2: null,
        postal_code: null,
        country_code: null,
        notes: null,
        color_hex: row.cells.color?.trim() || null,
        sort_order: row.sortOrder,
        status: row.cells.status || row.status,
      };
    case 'schedule-types':
      return {
        name: row.cells.name?.trim(),
        code: row.cells.code?.trim(),
        description: row.cells.description?.trim() || null,
        color_hex: row.cells.color?.trim() || null,
        sort_order: row.sortOrder,
        status: row.cells.status || row.status,
        is_system_seeded: false,
      };
    case 'service-lines': {
      const scheduleType = findByNameOrCode(lookups['schedule-types'], row.cells.scheduleType ?? '');
      const locationNames = splitScopeValues(row.cells.locations ?? '').filter((value) => normalizeText(value) !== 'companywide');
      const locationIds = locationNames
        .map((name) => findByNameOrCode(lookups.locations, name)?.id)
        .filter((value): value is number => Number.isInteger(value));

      return {
        schedule_type_id: scheduleType?.id,
        name: row.cells.name?.trim(),
        code: row.cells.code?.trim() || null,
        description: null,
        color_hex: row.cells.color?.trim() || null,
        sort_order: row.sortOrder,
        status: row.cells.status || row.status,
        coverage_mode: normalizeText(row.cells.locations ?? '') === 'companywide' ? 'companywide' : 'location_bound',
        location_ids: locationIds,
      };
    }
    case 'groups-teams': {
      const serviceLine = findByNameOrCode(lookups['service-lines'], row.cells.serviceLine ?? '');
      const locationIds = splitScopeValues(row.cells.location ?? '')
        .map((name) => findByNameOrCode(lookups.locations, name)?.id)
        .filter((value): value is number => Number.isInteger(value));

      return {
        service_line_id: serviceLine?.id,
        name: row.cells.name?.trim(),
        code: null,
        description: null,
        color_hex: row.cells.color?.trim() || null,
        sort_order: row.sortOrder,
        status: row.cells.status || row.status,
        location_ids: locationIds,
      };
    }
    case 'scheduler-admins': {
      const scheduleType = findByNameOrCode(lookups['schedule-types'], row.cells.scheduleType ?? '');
      const serviceLine = findByNameOrCode(lookups['service-lines'], row.cells.serviceLine ?? '');
      const location = findByNameOrCode(lookups.locations, row.cells.locationScope ?? '');
      const team = normalizeText(row.cells.teamScope ?? '') === 'all'
        ? undefined
        : findByNameOrCode(lookups.teams, row.cells.teamScope ?? '');

      return {
        schedule_type_id: scheduleType?.id,
        service_line_id: serviceLine?.id,
        location_id: location?.id ?? null,
        team_id: team?.id ?? null,
        schedule_id: null,
        scope_level: team ? 'team' : location ? 'location' : 'service_line',
        status: row.status === 'pending' ? 'draft' : row.status === 'deactivated' ? 'inactive' : 'active',
        handoff_status: row.status === 'active'
          ? 'active'
          : row.status === 'deactivated'
            ? 'complete'
            : 'pending_acceptance',
        notes: null,
        assignees: [
          {
            user_id: null,
            full_name: row.cells.name?.trim(),
            invite_email: row.cells.email?.trim(),
            invite_status: row.status === 'active'
              ? 'active'
              : row.status === 'deactivated'
                ? 'deactivated'
                : 'invited',
            company_membership_status: row.status === 'active' ? 'active' : 'invited',
            invite_sent_at: row.cells.inviteSentAt || now(),
            activated_at: row.cells.activatedAt || null,
            deactivated_at: row.cells.deactivatedAt || null,
            invite_expires_at: null,
            is_primary: true,
          },
        ],
        sort_order: existingRecordId ? undefined : row.sortOrder,
      };
    }
  }
};

const statusClassMap: Record<string, string> = {
  active: 'bg-success-transparent text-success',
  inactive: 'bg-warning-transparent text-warning',
  draft: 'bg-primary-transparent text-primary',
  archived: 'bg-dark-transparent text-dark',
  invited: 'bg-info-transparent text-info',
  pending: 'bg-info-transparent text-info',
  deactivated: 'bg-secondary-transparent text-secondary',
  expired: 'bg-warning-transparent text-warning',
};

const importStatusClassMap: Record<'valid' | 'warning' | 'error', string> = {
  valid: 'bg-success-transparent text-success',
  warning: 'bg-warning-transparent text-warning',
  error: 'bg-danger-transparent text-danger',
};

const sourceBadgeClassMap: Record<ColorSource, string> = {
  manual: 'bg-primary-transparent text-primary',
  inherited: 'bg-info-transparent text-info',
  mixed: 'bg-warning-transparent text-warning',
  default: 'bg-light text-muted',
};

const getColumnValue = (row: ManagementTableRow, columnKey: string) => {
  if (columnKey === 'status') {
    return row.status;
  }

  if (columnKey === 'lastUpdated') {
    return row.lastUpdated;
  }

  return row.cells[columnKey] ?? '';
};

const createEmptyRow = (config: ManagementTableSectionConfig): ManagementTableRow => {
  const cells = config.columns.reduce<Record<string, string>>((accumulator, column) => {
    if (column.key === 'lastUpdated') {
      return accumulator;
    }

    accumulator[column.key] = column.key === 'status' ? config.defaultStatus : '';
    return accumulator;
  }, {});

  if (config.id === 'scheduler-admins') {
    cells.inviteStatus = 'Invite pending review';
    cells.assignmentKind = 'new_invite';
    cells.inviteSentAt = '';
    cells.activatedAt = '';
    cells.deactivatedAt = '';
  }

  return {
    id: createRowId(config.id),
    status: config.defaultStatus,
    sortOrder: 0,
    lastUpdated: now(),
    dependencyWarning: null,
    cells,
  };
};

const summarizeRows = (config: ManagementTableSectionConfig, rows: ManagementTableRow[], store: ManagementTableStore) => {
  const warningRows = rows.filter((row) => row.dependencyWarning);
  const activeRows = rows.filter((row) => row.status === 'active').length;

  if (config.id === 'service-lines') {
    const blockedRows = rows.filter((row) => !getColumnValue(row, 'locations').trim());

    if (blockedRows.length > 0) {
      return {
        status: 'blocked' as OnboardingChecklistStatus,
        summary: `${rows.length} service lines in the current draft; ${blockedRows.length} still missing location or Companywide coverage.`,
        nextBestAction: 'Resolve uncovered service lines, then save the section and continue to teams or Scheduler Admin ownership.',
        blocker: blockedRows[0].dependencyWarning ?? 'At least one service line still needs coverage details.',
        warning: null,
      };
    }
  }

  if (config.id === 'scheduler-admins') {
    const coverage = buildSchedulerAdminCoverageSummary({
      ...store,
      'scheduler-admins': rows,
    });
    const pendingRows = rows.filter((row) => getSchedulerAdminRowStatus(row) === 'pending');
    const uncoveredActivationCount = coverage.uncoveredForActivation.length;
    const uncoveredFirstDraftCount = coverage.uncoveredForFirstDraft.length;

    return {
      status: uncoveredActivationCount > 0
        ? ('blocked' as OnboardingChecklistStatus)
        : pendingRows.length > 0 || uncoveredFirstDraftCount > 0
          ? ('waiting_on_scheduler_admin' as OnboardingChecklistStatus)
          : ('complete' as OnboardingChecklistStatus),
      summary: `${coverage.assignmentCount} Scheduler Admin assignments staged locally; activation coverage ${coverage.companyActivationCoveredCount}/${coverage.activeServiceLineCount}, active first-draft coverage ${coverage.firstDraftReadyCount}/${coverage.activeServiceLineCount}.`,
      nextBestAction: uncoveredActivationCount > 0
        ? 'Assign or reactivate coverage for every active service line before the company can be marked activation-ready.'
        : pendingRows.length > 0 || uncoveredFirstDraftCount > 0
          ? 'Track invite acceptance or reactivate an assignment so every service line has an active Scheduler Admin for first-draft readiness.'
          : 'Proceed to readiness review and downstream schedule setup.',
      blocker: uncoveredActivationCount > 0
        ? `${coverage.uncoveredForActivation[0]} no longer has pending or active Scheduler Admin coverage.`
        : null,
      warning: uncoveredFirstDraftCount > 0
        ? `${coverage.uncoveredForFirstDraft[0]} still lacks an active Scheduler Admin for first-draft readiness.`
        : pendingRows.length > 0
          ? 'Pending invites count for company activation, but not for first-draft readiness.'
          : null,
    };
  }

  if (config.id === 'groups-teams' && rows.length === 0) {
    return {
      status: 'complete' as OnboardingChecklistStatus,
      summary: 'No department or team rows are required in the current draft.',
      nextBestAction: 'Continue to Scheduler Admin assignments when no subgroups are needed.',
      blocker: null,
      warning: 'This section can remain empty when the company does not need team-level subdivisions.',
    };
  }

  return {
    status: warningRows.length > 0 ? ('needs_review' as OnboardingChecklistStatus) : ('complete' as OnboardingChecklistStatus),
    summary: `${rows.length} ${config.title.toLowerCase()} row${rows.length === 1 ? '' : 's'} saved locally, with ${activeRows} active and ${warningRows.length} warning${warningRows.length === 1 ? '' : 's'}.`,
    nextBestAction: warningRows.length > 0
      ? 'Review warning rows, then save the section status back to the backend readiness metadata.'
      : 'Save and continue when this section is ready for the next setup step.',
    blocker: null,
    warning: warningRows.length > 0 ? warningRows[0].dependencyWarning : null,
  };
};

const getValidationControlClass = (validation: ImportCellValidation) => {
  if (validation.status === 'error') {
    return 'border-danger';
  }

  if (validation.status === 'warning') {
    return 'border-warning';
  }

  return '';
};

const downloadTextFile = (filename: string, content: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const isCompanywideValue = (value: string) => {
  const normalized = value.trim().toLowerCase();
  return normalized === 'yes'
    || normalized === 'y'
    || normalized === 'true'
    || normalized === '1'
    || normalized === 'companywide'
    || normalized === 'company wide'
    || normalized === 'all locations'
    || normalized === 'all';
};

const toImportedManagementRow = (
  config: ManagementTableSectionConfig,
  cells: Record<string, string>,
  messages: string[]
) => {
  const row = createEmptyRow(config);
  const stamp = now();

  config.columns
    .filter((column) => column.editable)
    .forEach((column) => {
      if (column.key === 'lastUpdated') {
        return;
      }

      if (config.id === 'service-lines' && column.key === 'locations') {
        row.cells.locations = cells.locations.trim()
          ? cells.locations.trim()
          : isCompanywideValue(cells.companywide ?? '')
            ? 'Companywide'
            : '';
        return;
      }

      row.cells[column.key] = cells[column.key] ?? row.cells[column.key];
    });

  row.status = row.cells.status || config.defaultStatus;
  row.cells.status = row.status;
  row.lastUpdated = stamp;
  row.dependencyWarning = messages.length > 0 ? messages[0] : null;
  return row;
};

export const isManagementTableSectionId = (value: string): value is ManagementTableSectionId =>
  value in sectionConfigs;

type ColorFieldEditorProps = {
  colorMeta: ColorMeta;
  value: string;
  onChange: (value: string) => void;
};

const ColorFieldEditor: FC<ColorFieldEditorProps> = ({ colorMeta, value, onChange }) => {
  const normalizedValue = expandHexColor(value) ?? '';

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <Form.Control
          type="color"
          value={normalizedValue || '#0d6efd'}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          style={{ width: '3.25rem' }}
          aria-label="Choose color"
        />
        <Form.Control
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="#0D6EFD"
        />
        <Button size="sm" variant="light" onClick={() => onChange('')}>
          Reset
        </Button>
      </div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {colorPresets.map((preset) => (
          <Button
            key={preset.value}
            size="sm"
            variant="light"
            className="border"
            onClick={() => onChange(preset.value)}
          >
            <span
              className="rounded-circle border me-2 align-middle"
              style={{ display: 'inline-block', width: '0.875rem', height: '0.875rem', backgroundColor: preset.value }}
            ></span>
            {preset.label}
          </Button>
        ))}
      </div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        <Badge
          bg=""
          className={sourceBadgeClassMap[colorMeta.source]}
        >
          {colorMeta.sourceLabel}
        </Badge>
        {colorMeta.hex ? (
          <Badge
            bg=""
            className="border"
            style={{ backgroundColor: colorMeta.hex, color: colorMeta.recommendedTextColor }}
          >
            Preview {colorMeta.hex}
          </Badge>
        ) : (
          <Badge bg="" className="bg-light text-muted">
            No color selected
          </Badge>
        )}
      </div>
      <Form.Text className={colorMeta.accessibilityTone === 'warning' ? 'text-warning' : 'text-success'}>
        {colorMeta.accessibilityLabel} Color is always supplemental to labels and status badges.
      </Form.Text>
    </>
  );
};

const ManagementTableScaffold: FC<ManagementTableScaffoldProps> = ({ sectionId, tenantQuery, onSyncChecklist }) => {
  const config = sectionConfigs[sectionId];
  const resource = sectionIdToResource(sectionId);
  const importDefinition = getImportSectionDefinition(sectionId);
  const importFields = importDefinition.fields.filter((field) => !field.templateOnly);
  const [store, setStore] = useState(loadManagementTableStore);
  const [lookups, setLookups] = useState<ResourceLookupState>(emptyLookupState);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<'sortOrder' | string>('sortOrder');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [rowModalState, setRowModalState] = useState<RowModalState>(null);
  const [confirmModalState, setConfirmModalState] = useState<ConfirmModalState>(null);
  const [applyValueModalState, setApplyValueModalState] = useState<ApplyValueModalState>(null);
  const [editingDraft, setEditingDraft] = useState<ManagementTableRow | null>(null);
  const [applyColumnKey, setApplyColumnKey] = useState(config.columns.find((column) => column.bulkEditable)?.key ?? 'status');
  const [applyValue, setApplyValue] = useState('');
  const [lastOperationSummary, setLastOperationSummary] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [draggingRowId, setDraggingRowId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importInput, setImportInput] = useState('');
  const [parsedImportBatch, setParsedImportBatch] = useState<ParsedImportBatch | null>(null);
  const [importMappings, setImportMappings] = useState<string[]>([]);
  const [importOverrides, setImportOverrides] = useState<Record<number, Record<string, string>>>({});
  const [importSummary, setImportSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const rows = store[sectionId];

  useEffect(() => {
    persistStore(store);
  }, [store]);

  useEffect(() => {
    let cancelled = false;

    const loadResources = async () => {
      setLoading(true);
      setRequestError(null);

      try {
        const resources: Array<Exclude<AdminSettingsResource, 'schedules'>> = [
          'locations',
          'schedule-types',
          'service-lines',
          'teams',
          'scheduler-admin-scopes',
        ];

        const [responses, canonicalOverview] = await Promise.all([
          Promise.all(
            resources.map((resourceKey) =>
              fetchAdminSettingsResource(tenantQuery, resourceKey, { per_page: '100' })
            )
          ),
          sectionId === 'scheduler-admins' ? fetchAdminSettingsOverview(tenantQuery) : Promise.resolve(null),
        ]);

        if (cancelled) {
          return;
        }

        const nextLookups = responses.reduce<ResourceLookupState>((accumulator, response, index) => {
          const resourceKey = resources[index];
          accumulator[resourceKey] = response.data.data as ResourceLookupState[typeof resourceKey];
          return accumulator;
        }, emptyLookupState());

        setLookups(nextLookups);
        setStore((currentStore) => {
          const nextStore = { ...currentStore };

          resources.forEach((resourceKey) => {
            const storeSectionId = resourceToStoreSectionId(resourceKey);
            nextStore[storeSectionId] = nextLookups[resourceKey].map((record) =>
              toManagementRow(storeSectionId, record, nextLookups)
            );
          });

          if (canonicalOverview) {
            nextStore['scheduler-admins'] = buildCanonicalSchedulerAdminRows(canonicalOverview);
          }

          return nextStore;
        });
      } catch (error) {
        if (!cancelled) {
          setRequestError(error instanceof Error ? error.message : 'Unable to load Company Admin Settings records.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadResources();

    return () => {
      cancelled = true;
    };
  }, [sectionId, tenantQuery]);

  useEffect(() => {
    setSelectedRowIds([]);
    setSearchValue('');
    setStatusFilter('all');
    setSortKey('sortOrder');
    setSortDirection('asc');
    setApplyColumnKey(config.columns.find((column) => column.bulkEditable)?.key ?? 'status');
    setApplyValue('');
    setRowModalState(null);
    setConfirmModalState(null);
    setApplyValueModalState(null);
    setEditingDraft(null);
    setShowImportModal(false);
    setImportInput('');
    setParsedImportBatch(null);
    setImportMappings([]);
    setImportOverrides({});
    setImportSummary(null);
  }, [config, sectionId]);

  const loweredSearch = searchValue.trim().toLowerCase();
  const getRowWarning = (row: ManagementTableRow) =>
    sectionId === 'scheduler-admins' ? buildSchedulerAdminRowWarning(row, store) : row.dependencyWarning;
  const schedulerCoverageSummary = sectionId === 'scheduler-admins' ? buildSchedulerAdminCoverageSummary(store) : null;

  const visibleRows = [...rows.filter((row) => {
    const rowWarning = getRowWarning(row);

    if (statusFilter === 'warning' && !rowWarning) {
      return false;
    }

    if (statusFilter !== 'all' && statusFilter !== 'warning' && getSchedulerAdminRowStatus(row) !== statusFilter && row.status !== statusFilter) {
      return false;
    }

    if (!loweredSearch) {
      return true;
    }

    const haystack = [
      getSchedulerAdminRowStatus(row),
      rowWarning ?? '',
      row.lastUpdated,
      ...Object.values(row.cells),
    ].join(' ').toLowerCase();

    return haystack.includes(loweredSearch);
  })].sort((left, right) => {
    const leftValue = sortKey === 'sortOrder' ? `${left.sortOrder}` : getColumnValue(left, sortKey);
    const rightValue = sortKey === 'sortOrder' ? `${right.sortOrder}` : getColumnValue(right, sortKey);
    const comparison = leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: 'base' });

    return sortDirection === 'asc' ? comparison : comparison * -1;
  });

  const bulkEditableColumns = config.columns.filter((column) => column.bulkEditable);
  const warningRowCount = rows.filter((row) => Boolean(getRowWarning(row))).length;
  const manualOrderEnabled = sortKey === 'sortOrder';
  const editingColorMeta = editingDraft ? getColorMeta(sectionId, editingDraft, store) : null;
  const bulkApplyColorMeta = getColorMeta(sectionId, { cells: { color: applyValue } }, store);
  const importPreview = useMemo(() => {
    if (!parsedImportBatch) {
      return null;
    }

    return buildImportPreview(sectionId, parsedImportBatch, importMappings, importOverrides, rows, store);
  }, [importMappings, importOverrides, parsedImportBatch, rows, sectionId, store]);
  const visibleSelectionComplete = visibleRows.length > 0 && visibleRows.every((row) => selectedRowIds.includes(row.id));

  const updateRows = (updater: (currentRows: ManagementTableRow[]) => ManagementTableRow[]) => {
    setStore((currentStore) => ({
      ...currentStore,
      [sectionId]: sortRowsByOrder(updater(currentStore[sectionId])).map((row) =>
        sectionId === 'scheduler-admins' ? hydrateSchedulerAdminRow(row) : row
      ),
    }));
  };

  const resetImportState = () => {
    setImportInput('');
    setParsedImportBatch(null);
    setImportMappings([]);
    setImportOverrides({});
    setImportSummary(null);
  };

  const buildSchedulerAdminDraft = (draft: ManagementTableRow, stamp: string) => {
    const existingUser = findDirectoryUser(draft.cells.email ?? '');
    const assignmentKind = existingUser ? 'existing_user' : 'new_invite';
    const nextStatus = assignmentKind === 'existing_user' ? 'active' : 'pending';
    const latestEvent = assignmentKind === 'existing_user' ? 'Linked to existing user' : 'Invite sent';

    return {
      ...draft,
      status: nextStatus,
      lastUpdated: stamp,
      dependencyWarning: null,
      cells: {
        ...draft.cells,
        status: nextStatus,
        assignmentKind,
        inviteStatus: latestEvent,
        inviteSentAt: assignmentKind === 'new_invite' ? stamp : (draft.cells.inviteSentAt || stamp),
        activatedAt: assignmentKind === 'existing_user' ? stamp : '',
        deactivatedAt: '',
      },
    };
  };

  const buildRowLifecycleUpdate = (row: ManagementTableRow, action: 'resend' | 'deactivate' | 'reactivate') => {
    const stamp = now();
    const assignmentKind = getSchedulerAdminAssignmentKind(row);

    if (action === 'resend') {
      return {
        ...row,
        status: 'pending',
        lastUpdated: stamp,
        cells: {
          ...row.cells,
          status: 'pending',
          inviteStatus: row.cells.inviteSentAt ? 'Invite re-sent' : 'Invite sent',
          inviteSentAt: stamp,
          activatedAt: '',
        },
      };
    }

    if (action === 'deactivate') {
      return {
        ...row,
        status: 'deactivated',
        lastUpdated: stamp,
        cells: {
          ...row.cells,
          status: 'deactivated',
          inviteStatus: 'Deactivated',
          deactivatedAt: stamp,
        },
      };
    }

    return {
      ...row,
      status: assignmentKind === 'existing_user' ? 'active' : 'pending',
      lastUpdated: stamp,
      cells: {
        ...row.cells,
        status: assignmentKind === 'existing_user' ? 'active' : 'pending',
        inviteStatus: assignmentKind === 'existing_user' ? 'Reactivated' : 'Invite re-sent',
        inviteSentAt: assignmentKind === 'new_invite' ? stamp : row.cells.inviteSentAt,
        activatedAt: assignmentKind === 'existing_user' ? stamp : '',
        deactivatedAt: '',
      },
    };
  };

  const schedulerAdminPreview = sectionId === 'scheduler-admins' && editingDraft
    ? {
        existingUser: findDirectoryUser(editingDraft.cells.email ?? ''),
        scheduleType: editingDraft.cells.scheduleType?.trim() || 'Schedule type not set',
        serviceLine: editingDraft.cells.serviceLine?.trim() || 'Service line not set',
        locationScope: editingDraft.cells.locationScope?.trim() || 'All service-line locations',
        teamScope: editingDraft.cells.teamScope?.trim() || 'All departments',
        canSave: Boolean(
          editingDraft.cells.name?.trim()
          && editingDraft.cells.email?.trim()
          && editingDraft.cells.scheduleType?.trim()
          && editingDraft.cells.serviceLine?.trim()
        ),
      }
    : null;

  const openAddModal = () => {
    setEditingDraft(createEmptyRow(config));
    setRowModalState({ mode: 'add' });
  };

  const openEditModal = (rowId: string) => {
    const row = rows.find((candidate) => candidate.id === rowId);

    if (!row) {
      return;
    }

    setEditingDraft({
      ...row,
      cells: { ...row.cells },
    });
    setRowModalState({ mode: 'edit', rowId });
  };

  const saveRowModal = async () => {
    if (!editingDraft) {
      return;
    }

    const stamp = now();
    const normalizedRow = sectionId === 'scheduler-admins'
      ? buildSchedulerAdminDraft(editingDraft, stamp)
      : {
          ...editingDraft,
          status: editingDraft.cells.status || editingDraft.status,
          lastUpdated: stamp,
          cells: {
            ...editingDraft.cells,
            color: expandHexColor(editingDraft.cells.color ?? '') ?? editingDraft.cells.color ?? '',
            status: editingDraft.cells.status || editingDraft.status,
          },
        };

    setSaving(true);
    setRequestError(null);

    try {
      const payload = buildPayloadFromRow(sectionId, normalizedRow, lookups, rowModalState?.mode === 'edit' ? Number(rowModalState.rowId) : undefined);

      const response = rowModalState?.mode === 'edit' && rowModalState.rowId
        ? await updateAdminSettingsResource(tenantQuery, resource, Number(rowModalState.rowId), payload)
        : await createAdminSettingsResource(tenantQuery, resource, payload);

      const savedRow = toManagementRow(sectionId, response.record, lookups);

      updateRows((currentRows) => {
        if (rowModalState?.mode === 'edit' && rowModalState.rowId) {
          return currentRows.map((row) =>
            row.id === rowModalState.rowId
              ? { ...savedRow, sortOrder: row.sortOrder }
              : row
          );
        }

        return [
          ...currentRows,
          {
            ...savedRow,
            sortOrder: currentRows.length,
          },
        ];
      });

      if (sectionId === 'scheduler-admins') {
        const actionLabel = schedulerAdminPreview?.existingUser ? 'linked an existing user assignment for' : 'saved invite-ready coverage for';
        setLastOperationSummary(`${rowModalState?.mode === 'edit' ? 'Updated' : 'Saved'} 1 Scheduler Admin assignment and ${actionLabel} ${normalizedRow.cells.serviceLine || 'the selected service line'}.`);
      } else {
        setLastOperationSummary(`${rowModalState?.mode === 'edit' ? 'Updated' : 'Added'} 1 ${config.title.toLowerCase()} row.`);
      }

      setRowModalState(null);
      setEditingDraft(null);
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : `Unable to save ${config.title.toLowerCase()} row.`);
    } finally {
      setSaving(false);
    }
  };

  const applyBulkStatusChange = async (nextStatus: string) => {
    setSaving(true);
    setRequestError(null);

    try {
      const selectedRows = rows.filter((row) => selectedRowIds.includes(row.id));

      await Promise.all(selectedRows.map((row) => {
        const nextRow = {
          ...row,
          status: sectionId === 'scheduler-admins' ? toSchedulerAdminStatus(nextStatus) : nextStatus,
          cells: {
            ...row.cells,
            status: sectionId === 'scheduler-admins' ? toSchedulerAdminStatus(nextStatus) : nextStatus,
          },
        };

        return updateAdminSettingsResource(tenantQuery, resource, Number(row.id), buildPayloadFromRow(sectionId, nextRow, lookups, Number(row.id)));
      }));

      updateRows((currentRows) =>
        currentRows.map((row) =>
          selectedRowIds.includes(row.id)
            ? {
                ...row,
                status: sectionId === 'scheduler-admins' ? toSchedulerAdminStatus(nextStatus) : nextStatus,
                lastUpdated: now(),
                cells: { ...row.cells, status: sectionId === 'scheduler-admins' ? toSchedulerAdminStatus(nextStatus) : nextStatus },
              }
            : row
        )
      );

      setLastOperationSummary(`${selectedRowIds.length} row${selectedRowIds.length === 1 ? '' : 's'} updated to ${nextStatus}.`);
      setConfirmModalState(null);
      setSelectedRowIds([]);
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : 'Unable to update the selected rows.');
    } finally {
      setSaving(false);
    }
  };

  const applyBulkDelete = async () => {
    setSaving(true);
    setRequestError(null);

    try {
      await Promise.all(selectedRowIds.map((rowId) => deleteAdminSettingsResource(tenantQuery, resource, Number(rowId))));
      updateRows((currentRows) => currentRows.filter((row) => !selectedRowIds.includes(row.id)));
      setLastOperationSummary(`${selectedRowIds.length} row${selectedRowIds.length === 1 ? '' : 's'} deleted.`);
      setConfirmModalState(null);
      setSelectedRowIds([]);
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : 'Unable to delete the selected rows.');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectVisibleRows = (checked: boolean) => {
    if (!checked) {
      setSelectedRowIds([]);
      return;
    }

    setSelectedRowIds(visibleRows.map((row) => row.id));
  };

  const handleToggleRow = (rowId: string, checked: boolean) => {
    setSelectedRowIds((currentSelected) =>
      checked
        ? [...currentSelected, rowId]
        : currentSelected.filter((selectedRowId) => selectedRowId !== rowId)
    );
  };

  const handleSortChange = (columnKey: string) => {
    if (sortKey === columnKey) {
      setSortDirection((currentDirection) => currentDirection === 'asc' ? 'desc' : 'asc');
      return;
    }

    setSortKey(columnKey);
    setSortDirection('asc');
  };

  const handleResetRows = () => {
    setSelectedRowIds([]);
    setLastOperationSummary(`Reload this section from backend data using the company query in the current URL.`);
    setStore(loadManagementTableStore());
  };

  const handleSyncChecklist = async () => {
    const sync = summarizeRows(config, rows, store);
    const stamp = now();

    setSaving(true);
    setRequestError(null);

    try {
      await updateAdminSettingsSection(tenantQuery, config.checklistKey, {
        status: sync.status === 'draft_saved' ? 'draft' : sync.status,
        completion_percent: rows.length === 0 ? 0 : Math.min(100, Math.max(10, rows.filter((row) => row.status !== 'draft').length * 25)),
        blocker_summary: sync.blocker,
        draft_summary: {
          row_count: rows.length,
          last_saved_at: stamp,
        },
        validation_summary: sync.warning ? { warning: sync.warning } : null,
      });

      onSyncChecklist(config.checklistKey, {
        status: sync.status,
        summary: sync.summary,
        nextBestAction: sync.nextBestAction,
        blocker: sync.blocker,
        warning: sync.warning,
        lastSavedAt: stamp,
        activeSectionKey: config.checklistKey,
      });
      setLastSyncedAt(stamp);
      setLastOperationSummary(`Saved ${config.title.toLowerCase()} section status to backend readiness metadata.`);
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : 'Unable to sync this section status.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyValue = () => {
    if (!applyColumnKey || selectedRowIds.length === 0) {
      return;
    }

    const stamp = now();

    updateRows((currentRows) =>
      currentRows.map((row) => {
        if (!selectedRowIds.includes(row.id)) {
          return row;
        }

        const nextCells = {
          ...row.cells,
          [applyColumnKey]: applyColumnKey === 'color'
            ? expandHexColor(applyValue) ?? applyValue
            : applyValue,
        };
        const nextStatus = applyColumnKey === 'status'
          ? (sectionId === 'scheduler-admins' ? toSchedulerAdminStatus(applyValue) : applyValue)
          : row.status;

        return {
          ...row,
          status: nextStatus,
          cells: nextCells,
          lastUpdated: stamp,
        };
      })
    );

    setApplyValueModalState(null);
    setApplyValue('');
    setLastOperationSummary(`Applied 1 value to ${selectedRowIds.length} selected row${selectedRowIds.length === 1 ? '' : 's'}.`);
  };

  const handleCopyDown = () => {
    if (!applyColumnKey || selectedRowIds.length < 2) {
      return;
    }

    const sourceRow = rows.find((row) => row.id === selectedRowIds[0]);

    if (!sourceRow) {
      return;
    }

    const sourceValue = getColumnValue(sourceRow, applyColumnKey);
    const stamp = now();

    updateRows((currentRows) =>
      currentRows.map((row) => {
        if (!selectedRowIds.includes(row.id) || row.id === sourceRow.id) {
          return row;
        }

        const nextCells = {
          ...row.cells,
          [applyColumnKey]: sourceValue,
        };
        const nextStatus = applyColumnKey === 'status'
          ? (sectionId === 'scheduler-admins' ? toSchedulerAdminStatus(sourceValue) : sourceValue)
          : row.status;

        return {
          ...row,
          status: nextStatus,
          cells: nextCells,
          lastUpdated: stamp,
        };
      })
    );

    setApplyValueModalState(null);
    setLastOperationSummary(`Copied ${applyColumnKey} from the first selected row into ${selectedRowIds.length - 1} additional rows.`);
  };

  const handleSchedulerAdminLifecycleAction = async (rowId: string, action: 'resend' | 'deactivate' | 'reactivate') => {
    const currentRow = rows.find((row) => row.id === rowId);

    if (!currentRow) {
      return;
    }

    const nextRow = buildRowLifecycleUpdate(currentRow, action);
    setSaving(true);
    setRequestError(null);

    try {
      await updateAdminSettingsResource(tenantQuery, resource, Number(rowId), buildPayloadFromRow(sectionId, nextRow, lookups, Number(rowId)));
      updateRows((currentRows) =>
        currentRows.map((row) => row.id === rowId ? nextRow : row)
      );

      const labels: Record<typeof action, string> = {
        resend: 'Resent Scheduler Admin invite state',
        deactivate: 'Deactivated assignment',
        reactivate: 'Reactivated assignment',
      };

      setLastOperationSummary(`${labels[action]} for 1 Scheduler Admin row.`);
      setSelectedRowIds([]);
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : 'Unable to update Scheduler Admin coverage.');
    } finally {
      setSaving(false);
    }
  };

  const handleDragReorder = async (targetRowId: string) => {
    if (!draggingRowId || draggingRowId === targetRowId || !manualOrderEnabled) {
      return;
    }

    const orderedRows = sortRowsByOrder(rows);
    const sourceIndex = orderedRows.findIndex((row) => row.id === draggingRowId);
    const targetIndex = orderedRows.findIndex((row) => row.id === targetRowId);

    if (sourceIndex < 0 || targetIndex < 0) {
      return;
    }

    const nextRows = [...orderedRows];
    const [movedRow] = nextRows.splice(sourceIndex, 1);
    nextRows.splice(targetIndex, 0, movedRow);
    const reorderedRows = nextRows.map((row, index) => ({
      ...row,
      sortOrder: index,
      lastUpdated: row.id === movedRow.id ? now() : row.lastUpdated,
    }));

    setSaving(true);
    setRequestError(null);

    try {
      await reorderAdminSettingsResource(tenantQuery, resource, reorderedRows.map((row) => Number(row.id)));
      updateRows(() => reorderedRows);
      setDraggingRowId(null);
      setLastOperationSummary(`Reordered ${config.title.toLowerCase()} rows.`);
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : 'Unable to reorder rows.');
    } finally {
      setSaving(false);
    }
  };

  const handleParseImport = () => {
    const parsed = parseImportInput(sectionId, importInput);

    if (!parsed) {
      setParsedImportBatch(null);
      setImportMappings([]);
      setImportOverrides({});
      setImportSummary('Paste at least one non-empty row to start the preview workflow.');
      return;
    }

    setParsedImportBatch(parsed);
    setImportMappings(parsed.defaultMappings);
    setImportOverrides({});
    setImportSummary(parsed.summary);
  };

  const handleCommitImport = async () => {
    if (!importPreview || importPreview.rows.length === 0 || importPreview.counts.error > 0) {
      return;
    }

    const importedRows = importPreview.rows.map((previewRow, index) => ({
      ...toImportedManagementRow(config, previewRow.cells, previewRow.messages),
      sortOrder: rows.length + index,
    }));

    setSaving(true);
    setRequestError(null);

    try {
      const createdRows = await Promise.all(
        importedRows.map((row) =>
          createAdminSettingsResource(tenantQuery, resource, buildPayloadFromRow(sectionId, row, lookups))
        )
      );

      updateRows((currentRows) => [
        ...currentRows,
        ...createdRows.map((response) => toManagementRow(sectionId, response.record, lookups)),
      ]);
      setShowImportModal(false);
      resetImportState();
      setLastOperationSummary(`Imported ${importedRows.length} ${config.title.toLowerCase()} row${importedRows.length === 1 ? '' : 's'} through backend validation.`);
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : 'Unable to import the validated rows.');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadSample = () => {
    setImportInput(importDefinition.samplePaste);
    setImportSummary('Loaded the sample paste for this section. Parse it to review mappings and validation.');
  };

  const handleDownloadTemplate = () => {
    const filename = `${sectionId}-template.csv`;
    downloadTextFile(filename, buildTemplateCsv(sectionId));
    setImportSummary(`Downloaded ${filename}.`);
  };

  const handleImportMappingChange = (columnIndex: number, nextValue: string) => {
    setImportMappings((currentMappings) => currentMappings.map((value, index) => index === columnIndex ? nextValue : value));
  };

  const handleImportCellChange = (rowIndex: number, fieldKey: string, value: string) => {
    setImportOverrides((currentOverrides) => ({
      ...currentOverrides,
      [rowIndex]: {
        ...(currentOverrides[rowIndex] ?? {}),
        [fieldKey]: value,
      },
    }));
  };

  return (
    <Card className="custom-card">
      <Card.Header>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <div className="card-title">{sectionId === 'scheduler-admins' ? 'Current Scheduler Admins' : `${config.title} Workspace`}</div>
            <p className="text-muted mb-0">{sectionId === 'scheduler-admins' ? 'Review active, pending, deactivated, and expired Scheduler Admin access for this client.' : config.saveHint}</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {sectionId !== 'scheduler-admins' ? <Button size="sm" variant="primary" onClick={openAddModal} disabled={loading || saving}>
              {config.addLabel}
            </Button> : null}
            {sectionId !== 'scheduler-admins' ? <Button size="sm" variant="outline-primary" onClick={() => setShowImportModal(true)} disabled={loading || saving}>
              {config.importLabel}
            </Button> : null}
            <Button size="sm" variant="outline-primary" onClick={() => void handleSyncChecklist()} disabled={loading || saving}>
              Save section status
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={handleResetRows} disabled={loading || saving}>
              Reload current rows
            </Button>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <Alert variant="info" className="mb-3">
            Loading backend-backed {config.title.toLowerCase()} data.
          </Alert>
        ) : null}

        {requestError ? (
          <Alert variant="danger" className="mb-3">
            {requestError}
          </Alert>
        ) : null}

        {sectionId !== 'scheduler-admins' ? <Row className="g-3 mb-4">
          <Col xl={8}>
            <Row className="g-3">
              <Col md={4}>
                <Card className="custom-card border shadow-none mb-0 h-100">
                  <Card.Body>
                    <p className="text-muted fs-12 text-uppercase mb-2">Rows</p>
                    <h4 className="mb-1">{rows.length}</h4>
                    <p className="text-muted mb-0">{warningRowCount} with dependency warning</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="custom-card border shadow-none mb-0 h-100">
                  <Card.Body>
                  <p className="text-muted fs-12 text-uppercase mb-2">Import workflow</p>
                  <h4 className="mb-1">Instructions, preview, validation</h4>
                  <p className="text-muted mb-0">{config.spreadsheetHint}</p>
                </Card.Body>
              </Card>
              </Col>
              <Col md={4}>
                <Card className="custom-card border shadow-none mb-0 h-100">
                  <Card.Body>
                    <p className="text-muted fs-12 text-uppercase mb-2">Order mode</p>
                    <h4 className="mb-1">{config.orderMatters ? config.reorderLabel : 'Not required'}</h4>
                    <p className="text-muted mb-0">
                      {config.orderMatters
                        ? manualOrderEnabled
                          ? 'Drag the handle column to reorder rows.'
                          : 'Switch sort back to Manual order before drag-reordering.'
                        : 'This section keeps sort affordances but does not require manual ordering.'}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
          <Col xl={4}>
            <Alert variant="warning" className="mb-0 h-100">
              <div className="fw-semibold mb-2">Delete / deactivate boundary</div>
              <div className="text-muted">{config.destructiveHint}</div>
              {lastSyncedAt ? (
                <div className="mt-3 fs-13">
                  Checklist last synced: {formatSavedAt(lastSyncedAt)}
                </div>
              ) : null}
            </Alert>
          </Col>
        </Row> : null}

        {lastOperationSummary ? (
          <Alert variant="info">
            <div className="fw-semibold mb-1">Latest local action</div>
            <div>{lastOperationSummary}</div>
          </Alert>
        ) : null}

        {schedulerCoverageSummary && sectionId !== 'scheduler-admins' ? (
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Card className="custom-card border shadow-none mb-0 h-100">
                <Card.Body>
                  <p className="text-muted fs-12 text-uppercase mb-2">Company activation coverage</p>
                  <h4 className="mb-1">{schedulerCoverageSummary.companyActivationCoveredCount}/{schedulerCoverageSummary.activeServiceLineCount}</h4>
                  <p className="text-muted mb-0">Pending or active assignments count. Deactivated rows do not.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="custom-card border shadow-none mb-0 h-100">
                <Card.Body>
                  <p className="text-muted fs-12 text-uppercase mb-2">First-draft readiness</p>
                  <h4 className="mb-1">{schedulerCoverageSummary.firstDraftReadyCount}/{schedulerCoverageSummary.activeServiceLineCount}</h4>
                  <p className="text-muted mb-0">Only active Scheduler Admins count toward first-draft readiness.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="custom-card border shadow-none mb-0 h-100">
                <Card.Body>
                  <p className="text-muted fs-12 text-uppercase mb-2">Lifecycle states</p>
                  <h4 className="mb-1">{schedulerCoverageSummary.pendingCount} pending, {schedulerCoverageSummary.activeCount} active</h4>
                  <p className="text-muted mb-0">{schedulerCoverageSummary.deactivatedCount} deactivated, {schedulerCoverageSummary.expiredCount} expired</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : null}

        {schedulerCoverageSummary?.uncoveredForActivation.length ? (
          <Alert variant="danger" className="mb-3">
            <div className="fw-semibold mb-1">Coverage blocker</div>
            <div>{schedulerCoverageSummary.uncoveredForActivation[0]} currently has no pending or active Scheduler Admin coverage.</div>
          </Alert>
        ) : null}

        <Row className="g-3 align-items-end mb-3">
          <Col md={5} xl={4}>
            <Form.Group>
              <Form.Label className="mb-1">Search</Form.Label>
              <Form.Control
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder={`Search ${config.title.toLowerCase()} rows`}
              />
            </Form.Group>
          </Col>
          <Col md={3} xl={2}>
            <Form.Group>
              <Form.Label className="mb-1">Filter</Form.Label>
              <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                {config.filters.map((filterOption) => (
                  <option key={filterOption.value} value={filterOption.value}>
                    {filterOption.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          {sectionId !== 'scheduler-admins' ? <Col md={4} xl={6}>
            <div className="d-flex flex-wrap gap-2 justify-content-md-end">
              <Button size="sm" variant="outline-primary" onClick={() => setApplyValueModalState({ mode: 'apply-selected' })} disabled={selectedRowIds.length === 0}>
                Update selected rows
              </Button>
              <Button size="sm" variant="outline-primary" onClick={() => setApplyValueModalState({ mode: 'copy-down' })} disabled={selectedRowIds.length < 2}>
                Match first selected row
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setShowImportModal(true)}>
                Review import
              </Button>
            </div>
          </Col> : null}
        </Row>

        {sectionId !== 'scheduler-admins' && selectedRowIds.length > 0 ? (
          <Alert variant="light" className="border">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
              <div>
                <div className="fw-semibold mb-1">Selected rows</div>
                <div className="text-muted">
                  {selectedRowIds.length} row{selectedRowIds.length === 1 ? '' : 's'} selected.
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Button size="sm" variant="outline-danger" onClick={() => setConfirmModalState({ mode: 'delete', rowIds: selectedRowIds })}>
                  {config.deleteLabel}
                </Button>
                <Button size="sm" variant="outline-warning" onClick={() => setConfirmModalState({ mode: 'deactivate', rowIds: selectedRowIds })}>
                  {config.deactivateLabel}
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setSelectedRowIds([])}>
                  Clear selection
                </Button>
              </div>
            </div>
          </Alert>
        ) : null}

        <div className="table-responsive">
          <Table className="text-nowrap align-middle mb-0">
            <thead>
              <tr>
                {sectionId !== 'scheduler-admins' ? <th style={{ width: '4rem' }}>
                  <Form.Check
                    type="checkbox"
                    checked={visibleSelectionComplete}
                    onChange={(event) => handleSelectVisibleRows(event.target.checked)}
                    aria-label="Select all visible rows"
                  />
                </th> : null}
                {sectionId !== 'scheduler-admins' ? <th style={{ width: '5rem' }}>Order</th> : null}
                {config.columns.map((column) => (
                  <th key={column.key} style={column.width ? { width: column.width } : undefined}>
                    {column.sortable ? (
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 text-decoration-none text-body"
                        onClick={() => handleSortChange(column.key === 'lastUpdated' ? 'lastUpdated' : column.key)}
                      >
                        {column.label}
                        <i className={`ms-2 ${sortKey === column.key ? (sortDirection === 'asc' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line') : 'ri-expand-up-down-line'}`}></i>
                      </Button>
                    ) : column.label}
                  </th>
                ))}
                {sectionId !== 'scheduler-admins' ? <th style={{ width: '18%' }}>Warnings / actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr
                  key={row.id}
                  onDragOver={(event) => {
                    if (manualOrderEnabled) {
                      event.preventDefault();
                    }
                  }}
                  onDrop={() => handleDragReorder(row.id)}
                >
                  {sectionId !== 'scheduler-admins' ? <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedRowIds.includes(row.id)}
                      onChange={(event) => handleToggleRow(row.id, event.target.checked)}
                      aria-label={`Select ${getColumnValue(row, config.columns[0].key) || 'row'}`}
                    />
                  </td> : null}
                  {sectionId !== 'scheduler-admins' ? <td>
                    <div
                      className={`d-inline-flex align-items-center gap-2 ${manualOrderEnabled && config.orderMatters ? 'cursor-grab' : 'opacity-50'}`}
                      draggable={manualOrderEnabled && config.orderMatters}
                      onDragStart={() => setDraggingRowId(row.id)}
                    >
                      <i className="ri-draggable"></i>
                      <span className="text-muted fs-12">{row.sortOrder + 1}</span>
                    </div>
                  </td> : null}
                  {config.columns.map((column) => (
                    <td key={`${row.id}-${column.key}`}>
                      {column.key === 'status' ? (
                        <Badge bg="" className={statusClassMap[getSchedulerAdminRowStatus(row)] ?? 'bg-light text-muted'}>
                          {getSchedulerAdminRowStatus(row)}
                        </Badge>
                      ) : column.type === 'color' ? (
                        <div className="d-flex flex-column gap-2">
                          {(() => {
                            const colorMeta = getColorMeta(sectionId, row, store);
                            return (
                              <>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  <Badge
                                    bg=""
                                    className="border"
                                    style={{
                                      backgroundColor: colorMeta.hex ?? '#f8f9fa',
                                      color: colorMeta.hex ? colorMeta.recommendedTextColor : '#6c757d',
                                    }}
                                  >
                                    {colorMeta.hex ?? 'No color'}
                                  </Badge>
                                  <Badge bg="" className={sourceBadgeClassMap[colorMeta.source]}>
                                    {colorMeta.sourceLabel}
                                  </Badge>
                                </div>
                                <span className={`fs-12 ${colorMeta.accessibilityTone === 'warning' ? 'text-warning' : 'text-muted'}`}>
                                  {colorMeta.accessibilityLabel}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      ) : column.key === 'lastUpdated' ? (
                        <span className="text-muted">{formatSavedAt(row.lastUpdated)}</span>
                      ) : (
                        <span>{getColumnValue(row, column.key) || <span className="text-muted">Not set</span>}</span>
                      )}
                    </td>
                  ))}
                  {sectionId !== 'scheduler-admins' ? <td>
                    <div className="d-flex flex-column gap-2">
                      {getRowWarning(row) ? (
                        <Alert variant="warning" className="py-2 px-3 mb-0">
                          <div className="fw-semibold fs-12 mb-1">Dependency preview</div>
                          <div className="fs-13">{getRowWarning(row)}</div>
                        </Alert>
                      ) : (
                        <span className="text-muted fs-13">No dependency warning</span>
                      )}
                      {row.cells.recordSource === 'canonical_group_roles' ? (
                        <Alert variant="info" className="py-2 px-3 mb-0">
                          Department access automatically covers every child Schedule Group. Direct group access remains listed separately.
                        </Alert>
                      ) : <div className="d-flex flex-wrap gap-2">
                        <Button size="sm" variant="light" onClick={() => openEditModal(row.id)}>
                          Edit
                        </Button>
                        {sectionId === 'scheduler-admins' && getSchedulerAdminRowStatus(row) === 'pending' ? (
                          <Button size="sm" variant="outline-primary" onClick={() => handleSchedulerAdminLifecycleAction(row.id, 'resend')}>
                            Resend invite
                          </Button>
                        ) : null}
                        {sectionId === 'scheduler-admins' && (getSchedulerAdminRowStatus(row) === 'deactivated' || getSchedulerAdminRowStatus(row) === 'expired') ? (
                          <Button size="sm" variant="outline-success" onClick={() => handleSchedulerAdminLifecycleAction(row.id, 'reactivate')}>
                            {getSchedulerAdminAssignmentKind(row) === 'existing_user' ? 'Reactivate' : 'Re-invite'}
                          </Button>
                        ) : null}
                        <Button size="sm" variant="outline-warning" onClick={() => {
                          if (sectionId === 'scheduler-admins') {
                            handleSchedulerAdminLifecycleAction(row.id, 'deactivate');
                            return;
                          }
                          setSelectedRowIds([row.id]);
                          setConfirmModalState({ mode: 'deactivate', rowIds: [row.id] });
                        }}>
                          Deactivate
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => {
                          setSelectedRowIds([row.id]);
                          setConfirmModalState({ mode: 'delete', rowIds: [row.id] });
                        }}>
                          Delete
                        </Button>
                      </div>}
                    </div>
                  </td> : null}
                </tr>
              ))}
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={config.columns.length + (sectionId === 'scheduler-admins' ? 0 : 3)}>
                    <div className="text-center text-muted py-4">
                      No rows match the current search/filter combination.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>

        {sectionId === 'scheduler-admins' ? (
          <>
            <Card className="custom-card border shadow-none mt-4 mb-0">
              <Card.Header>
                <div className="card-title">Grant Scheduler Admin Access</div>
              </Card.Header>
              <Card.Body className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
                <div>
                  <p className="mb-1 fw-semibold">Assign an existing user or prepare a new Scheduler Admin invitation.</p>
                  <p className="text-muted mb-0">Grant Department-wide access or choose one Schedule Group for narrower access.</p>
                </div>
                <Button variant="primary" onClick={openAddModal} disabled={loading || saving}>
                  Grant access
                </Button>
              </Card.Body>
            </Card>
            <Alert variant="info" className="mt-3 mb-0">
              <div className="fw-semibold mb-1">How Scheduler Admin permissions work</div>
              <div>Department access automatically includes every current and future child Schedule Group. Direct Group access is narrower and can be combined with Department access. This workflow never grants Platform Admin access.</div>
            </Alert>
          </>
        ) : null}

        {sectionId !== 'scheduler-admins' ? <Row className="g-3 mt-4">
          <Col xl={7}>
            <Card className="custom-card border shadow-none mb-0 h-100">
              <Card.Header>
                <div className="card-title">Import Review Rules</div>
              </Card.Header>
              <Card.Body>
                <ListGroup variant="flush">
                  <ListGroup.Item className="px-0">
                    <span className="text-muted">Paste from spreadsheet, CSV-like rows, or simple newline lists into a preview gate before rows are saved into the active Company Admin Settings table.</span>
                  </ListGroup.Item>
                  <ListGroup.Item className="px-0">
                    <span className="text-muted">Alias mapping, duplicate checks, relationship validation, and inline preview correction happen before rows are committed to the active Company Admin Settings record set.</span>
                  </ListGroup.Item>
                  <ListGroup.Item className="px-0">
                    <span className="text-muted">Imported rows remain in preview until you review and save them. Invite or email delivery does not happen here, so Scheduler Admin handoff remains a draft review state.</span>
                  </ListGroup.Item>
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
          <Col xl={5}>
            <Card className="custom-card border shadow-none mb-0 h-100">
              <Card.Header>
                <div className="card-title">Section Signals</div>
              </Card.Header>
              <Card.Body>
                <ListGroup variant="flush">
                  <ListGroup.Item className="px-0 d-flex justify-content-between gap-3">
                    <span className="text-muted">Selected rows</span>
                    <span>{selectedRowIds.length}</span>
                  </ListGroup.Item>
                  <ListGroup.Item className="px-0 d-flex justify-content-between gap-3">
                    <span className="text-muted">Visible rows</span>
                    <span>{visibleRows.length}</span>
                  </ListGroup.Item>
                  <ListGroup.Item className="px-0 d-flex justify-content-between gap-3">
                    <span className="text-muted">Dependency warnings</span>
                    <span>{warningRowCount}</span>
                  </ListGroup.Item>
                  <ListGroup.Item className="px-0 d-flex justify-content-between gap-3">
                    <span className="text-muted">Manual order</span>
                    <span>{manualOrderEnabled ? 'Enabled' : 'Sorted by another column'}</span>
                  </ListGroup.Item>
                  {schedulerCoverageSummary ? (
                    <ListGroup.Item className="px-0 d-flex justify-content-between gap-3">
                      <span className="text-muted">Uncovered service lines</span>
                      <span>{schedulerCoverageSummary.uncoveredForActivation.length}</span>
                    </ListGroup.Item>
                  ) : null}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row> : null}
      </Card.Body>

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{config.importLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <div className="fw-semibold mb-1">Accepted in v1</div>
            <div>{importDefinition.acceptedFormats.join(' | ')}</div>
            <div className="mt-2 text-muted">{importDefinition.followUpNote}</div>
          </Alert>

          <Row className="g-3 mb-3">
            <Col lg={4}>
              <Card className="custom-card border shadow-none mb-0 h-100">
                <Card.Header>
                  <div className="card-title">Required columns</div>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    {importFields.filter((field) => field.required).map((field) => (
                      <ListGroup.Item key={field.key} className="px-0">
                        <span className="fw-semibold">{field.label}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="custom-card border shadow-none mb-0 h-100">
                <Card.Header>
                  <div className="card-title">Optional columns</div>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    {importFields.filter((field) => !field.required).map((field) => (
                      <ListGroup.Item key={field.key} className="px-0">
                        <span>{field.label}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="custom-card border shadow-none mb-0 h-100">
                <Card.Header>
                  <div className="card-title">Formatting rules</div>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    {importDefinition.generalRules.map((rule) => (
                      <ListGroup.Item key={rule} className="px-0">
                        <span className="text-muted">{rule}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button size="sm" variant="outline-primary" onClick={handleDownloadTemplate}>
              Download CSV template
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={handleLoadSample}>
              Load example paste
            </Button>
            <Button size="sm" variant="primary" onClick={handleParseImport} disabled={!importInput.trim()}>
              Parse into preview
            </Button>
            <Button size="sm" variant="light" onClick={resetImportState}>
              Clear import state
            </Button>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Paste data</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={importInput}
              onChange={(event) => setImportInput(event.target.value)}
              placeholder={`Paste ${config.title.toLowerCase()} rows here. Headers are recommended but not required.`}
            />
            <Form.Text>
              Auto-mapping uses deterministic aliases where possible. You can remap incoming columns before commit.
            </Form.Text>
          </Form.Group>

          {importSummary ? (
            <Alert variant={parsedImportBatch ? 'secondary' : 'warning'} className="mb-3">
              {importSummary}
            </Alert>
          ) : null}

          {parsedImportBatch ? (
            <>
              <Card className="custom-card border shadow-none mb-3">
                <Card.Header>
                  <div className="card-title">Column mapping</div>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    {parsedImportBatch.columnLabels.map((label, index) => (
                      <Col md={6} xl={4} key={`${label}-${index}`}>
                        <Form.Group>
                          <Form.Label>{label}</Form.Label>
                          <Form.Select
                            value={importMappings[index] ?? IMPORT_IGNORE_FIELD}
                            onChange={(event) => handleImportMappingChange(index, event.target.value)}
                          >
                            <option value={IMPORT_IGNORE_FIELD}>Ignore this column</option>
                            {importFields.map((field) => (
                              <option key={field.key} value={field.key}>
                                {field.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>

              {importPreview ? (
                <>
                  <Row className="g-3 mb-3">
                    <Col md={3}>
                      <Card className="custom-card border shadow-none mb-0">
                        <Card.Body>
                          <p className="text-muted fs-12 text-uppercase mb-2">Valid</p>
                          <h4 className="mb-0">{importPreview.counts.valid}</h4>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="custom-card border shadow-none mb-0">
                        <Card.Body>
                          <p className="text-muted fs-12 text-uppercase mb-2">Warnings</p>
                          <h4 className="mb-0">{importPreview.counts.warning}</h4>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="custom-card border shadow-none mb-0">
                        <Card.Body>
                          <p className="text-muted fs-12 text-uppercase mb-2">Errors</p>
                          <h4 className="mb-0">{importPreview.counts.error}</h4>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="custom-card border shadow-none mb-0">
                        <Card.Body>
                          <p className="text-muted fs-12 text-uppercase mb-2">Committable rows</p>
                          <h4 className="mb-0">{importPreview.counts.error === 0 ? importPreview.committableCount : 0}</h4>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {importPreview.mappingWarnings.length > 0 ? (
                    <Alert variant="warning">
                      <div className="fw-semibold mb-1">Required mapping gaps</div>
                      <ul className="mb-0 ps-3">
                        {importPreview.mappingWarnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    </Alert>
                  ) : null}

                  <Alert variant={importPreview.counts.error > 0 ? 'warning' : 'success'}>
                    {importPreview.counts.error > 0
                      ? 'Resolve preview errors before commit. Warning rows can stay, but error rows block the batch.'
                      : 'Preview rows are ready to commit into the current draft table. Warning rows will keep their first warning as a dependency note.'}
                  </Alert>

                  <div className="table-responsive">
                    <Table className="align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: '8rem' }}>Status</th>
                          {importFields.map((field) => (
                            <th key={field.key}>{field.label}</th>
                          ))}
                          <th style={{ width: '20rem' }}>Validation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.rows.map((previewRow) => (
                          <tr key={`preview-${previewRow.rowIndex}`}>
                            <td>
                              <Badge bg="" className={importStatusClassMap[previewRow.status]}>
                                {previewRow.status}
                              </Badge>
                            </td>
                            {importFields.map((field) => (
                              <td key={`${previewRow.rowIndex}-${field.key}`}>
                                <Form.Control
                                  value={previewRow.cells[field.key] ?? ''}
                                  className={getValidationControlClass(previewRow.cellValidation[field.key])}
                                  onChange={(event) => handleImportCellChange(previewRow.rowIndex, field.key, event.target.value)}
                                  placeholder={field.inputPlaceholder}
                                />
                                {previewRow.cellValidation[field.key].messages.map((message) => (
                                  <div
                                    key={`${previewRow.rowIndex}-${field.key}-${message}`}
                                    className={`fs-12 mt-1 ${previewRow.cellValidation[field.key].status === 'error' ? 'text-danger' : previewRow.cellValidation[field.key].status === 'warning' ? 'text-warning' : 'text-muted'}`}
                                  >
                                    {message}
                                  </div>
                                ))}
                              </td>
                            ))}
                            <td>
                              {previewRow.messages.length > 0 ? (
                                <ListGroup variant="flush">
                                  {previewRow.messages.map((message) => (
                                    <ListGroup.Item key={`${previewRow.rowIndex}-${message}`} className="px-0 py-1 border-0">
                                      <span className={previewRow.status === 'error' ? 'text-danger' : previewRow.status === 'warning' ? 'text-warning' : 'text-muted'}>
                                        {message}
                                      </span>
                                    </ListGroup.Item>
                                  ))}
                                </ListGroup>
                              ) : (
                                <span className="text-muted">No validation issues</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <div className="text-muted fs-13">
            Checklist save/sync still happens from the main table card after imported rows land in the current draft.
          </div>
          <div className="d-flex gap-2">
            <Button variant="light" onClick={() => setShowImportModal(false)}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleCommitImport}
              disabled={!importPreview || importPreview.rows.length === 0 || importPreview.counts.error > 0}
            >
              Commit valid rows
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(rowModalState)} onHide={() => setRowModalState(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{rowModalState?.mode === 'edit' ? `Edit ${config.title} Row` : config.addLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingDraft ? (
            sectionId === 'scheduler-admins' ? (
              <>
                <Alert variant="info" className="mb-3">
                  This modal saves Company Admin assignment coverage through the current workspace. Linking existing users and sending invites still depend on downstream membership and invite flows.
                </Alert>
                <Row className="g-3">
                  {config.columns.filter((column) => column.editable).map((column) => (
                    <Col md={6} key={column.key}>
                      <Form.Group>
                        <Form.Label>{column.label}</Form.Label>
                        <Form.Control
                          type={column.type === 'email' ? 'email' : 'text'}
                          value={editingDraft.cells[column.key] ?? ''}
                          onChange={(event) => setEditingDraft({
                            ...editingDraft,
                            cells: {
                              ...editingDraft.cells,
                              [column.key]: event.target.value,
                            },
                          })}
                        />
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
                {schedulerAdminPreview ? (
                  <Card className="custom-card border shadow-none mt-4">
                    <Card.Header>
                      <div className="card-title">Permission Preview Before Save</div>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        <Badge bg="" className={`bg-${schedulerAdminPreview.existingUser ? 'success' : 'info'}-transparent text-${schedulerAdminPreview.existingUser ? 'success' : 'info'}`}>
                          {schedulerAdminPreview.existingUser ? 'Link existing user' : 'Invite new Scheduler Admin'}
                        </Badge>
                        <Badge bg="" className="bg-light text-muted">Company-only scope</Badge>
                        <Badge bg="" className="bg-light text-muted">No Platform Admin grant</Badge>
                      </div>
                      <p className="mb-2">{schedulerAdminPreview.scheduleType}{' -> '}{schedulerAdminPreview.serviceLine}</p>
                      <p className="text-muted mb-1">Location scope: {schedulerAdminPreview.locationScope}</p>
                      <p className="text-muted mb-3">Department/Team scope: {schedulerAdminPreview.teamScope}</p>
                      {schedulerAdminPreview.existingUser ? (
                        <Alert variant="success" className="mb-0">
                          <div className="fw-semibold mb-1">{schedulerAdminPreview.existingUser.name}</div>
                          <div className="text-muted">{schedulerAdminPreview.existingUser.note}</div>
                        </Alert>
                      ) : (
                        <Alert variant="warning" className="mb-0">
                          <div className="fw-semibold mb-1">New invite path</div>
                          <div className="text-muted">Saving this row records pending assignment coverage and marks the handoff as pending review. No invite or email is sent from this screen.</div>
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                ) : null}
              </>
            ) : (
              <Row className="g-3">
                {config.columns.filter((column) => column.editable).map((column) => (
                  <Col md={column.type === 'text' ? 6 : 4} key={column.key}>
                    <Form.Group>
                      <Form.Label>{column.label}</Form.Label>
                      {column.key === 'status' ? (
                        <Form.Select
                          value={editingDraft.cells.status || editingDraft.status}
                          onChange={(event) => setEditingDraft({
                            ...editingDraft,
                            status: event.target.value,
                            cells: {
                              ...editingDraft.cells,
                              status: event.target.value,
                            },
                          })}
                        >
                          {config.statusOptions.map((statusOption) => (
                            <option key={statusOption} value={statusOption}>{statusOption}</option>
                          ))}
                        </Form.Select>
                      ) : column.type === 'color' && editingColorMeta ? (
                        <ColorFieldEditor
                          colorMeta={editingColorMeta}
                          value={editingDraft.cells[column.key] ?? ''}
                          onChange={(value) => setEditingDraft({
                            ...editingDraft,
                            cells: {
                              ...editingDraft.cells,
                              [column.key]: value,
                            },
                          })}
                        />
                      ) : (
                        <Form.Control
                          type={column.type === 'email' ? 'email' : 'text'}
                          value={editingDraft.cells[column.key] ?? ''}
                          onChange={(event) => setEditingDraft({
                            ...editingDraft,
                            cells: {
                              ...editingDraft.cells,
                              [column.key]: event.target.value,
                            },
                          })}
                        />
                      )}
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            )
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setRowModalState(null)}>Cancel</Button>
          <Button onClick={saveRowModal} disabled={sectionId === 'scheduler-admins' ? !schedulerAdminPreview?.canSave : false}>
            {sectionId === 'scheduler-admins'
              ? schedulerAdminPreview?.existingUser ? 'Link assignment' : 'Save & send invite'
              : 'Save row'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(confirmModalState)} onHide={() => setConfirmModalState(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{confirmModalState?.mode === 'delete' ? config.deleteLabel : config.deactivateLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmModalState ? (
            <Alert variant={confirmModalState.mode === 'delete' ? 'danger' : 'warning'} className="mb-0">
              <div className="fw-semibold mb-1">
                {confirmModalState.rowIds.length} row{confirmModalState.rowIds.length === 1 ? '' : 's'} selected
              </div>
              <div className="text-muted">
                {confirmModalState.mode === 'delete'
                  ? 'Delete removes these rows from the current draft table immediately.'
                  : 'Deactivate keeps the rows in place but updates status so downstream review can see the change.'}
              </div>
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setConfirmModalState(null)}>Cancel</Button>
          {confirmModalState?.mode === 'delete' ? (
            <Button variant="danger" onClick={applyBulkDelete}>Delete rows</Button>
          ) : (
            <Button variant="warning" onClick={() => applyBulkStatusChange(sectionId === 'scheduler-admins' ? 'deactivated' : 'inactive')}>Deactivate rows</Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(applyValueModalState)} onHide={() => setApplyValueModalState(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{applyValueModalState?.mode === 'copy-down' ? 'Match selected rows to the first row' : 'Update selected rows'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Column</Form.Label>
                <Form.Select value={applyColumnKey} onChange={(event) => setApplyColumnKey(event.target.value)}>
                  {bulkEditableColumns.map((column) => (
                    <option key={column.key} value={column.key}>{column.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {applyValueModalState?.mode === 'apply-selected' ? (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Value</Form.Label>
                  {applyColumnKey === 'status' ? (
                    <Form.Select value={applyValue} onChange={(event) => setApplyValue(event.target.value)}>
                      <option value="">Select status</option>
                      {config.statusOptions.map((statusOption) => (
                        <option key={statusOption} value={statusOption}>{statusOption}</option>
                      ))}
                    </Form.Select>
                  ) : applyColumnKey === 'color' ? (
                    <ColorFieldEditor
                      colorMeta={bulkApplyColorMeta}
                      value={applyValue}
                      onChange={setApplyValue}
                    />
                  ) : (
                    <Form.Control value={applyValue} onChange={(event) => setApplyValue(event.target.value)} />
                  )}
                </Form.Group>
              </Col>
            ) : null}
          </Row>
          {applyValueModalState?.mode === 'copy-down' ? (
            <Alert variant="secondary" className="mt-3 mb-0">
              Use the first selected row as the source. Every other selected row gets the same value in this field.
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setApplyValueModalState(null)}>Cancel</Button>
          {applyValueModalState?.mode === 'copy-down' ? (
            <Button onClick={handleCopyDown}>Match selected rows</Button>
          ) : (
            <Button onClick={handleApplyValue} disabled={!applyColumnKey || !applyValue}>Update rows</Button>
          )}
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ManagementTableScaffold;
